/**
 * src/data/glossaryVietLai.js: bản viết lại, dày hơn, cho các trang từ điển
 * đang có lượt hiển thị trên Google (Search Console, 10/2026).
 *
 * Các truy vấn kéo những trang này lên là dạng "… là gì": pre seed, pitch deck,
 * slide deck, vesting, cap table, gọi vốn. Trang gốc khoảng 400 chữ, nằm ở
 * vị trí trung bình 16 (trang 2). Bản này trả lời đủ các câu hỏi kế tiếp mà
 * người tìm thường gõ tiếp, có bảng và ví dụ số, và nối sang trang liên quan.
 *
 * Cú pháp liên kết trong câu: xem src/lib/lienKetChu.js.
 */

export const VIET_LAI = {
  'vong-pre-seed': {
    capNhat: '2026-10-12',
    lienQuan: ['vong-seed', 'nha-dau-tu-thien-than', 'safe-la-gi', 'so-tien-goi-von', 'mvp-la-gi', 'traction-la-gi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Pre-seed là vòng gọi vốn có tổ chức đầu tiên của một startup, thường diễn ra khi đã có sản phẩm chạy được (ít nhất là một [[mvp-la-gi|MVP]]) và vài dấu hiệu ban đầu cho thấy có người cần, nhưng chưa có doanh thu đáng kể.',
          'Ở Việt Nam, một vòng pre-seed thường từ một tới vài tỷ đồng, đôi khi lên trên chục tỷ với đội ngũ mạnh. Tiền này dùng để đi tới cột mốc đủ thuyết phục cho [[vong-seed|vòng seed]], thường trong 12 tới 18 tháng.',
        ],
      },
      {
        h: 'Pre-seed, angel và seed khác nhau thế nào',
        p: [
          'Ba cái tên hay bị dùng lẫn. Ranh giới không cố định, nhưng khác biệt chính nằm ở mức bằng chứng bạn có trong tay và kiểu nhà đầu tư ngồi đối diện.',
        ],
        bang: {
          chuThich: 'Mức thường gặp ở Việt Nam, chỉ để định hướng',
          cot: ['', 'Vòng angel', 'Vòng pre-seed', 'Vòng seed'],
          hang: [
            [
              'Đang có gì',
              'Ý tưởng, đội ngũ, có thể có bản mẫu',
              'Sản phẩm chạy được, vài khách dùng thử hoặc trả tiền',
              'Doanh thu đang tăng, số liệu giữ chân khách',
            ],
            [
              'Ai đầu tư',
              '[[nha-dau-tu-thien-than|Nhà đầu tư thiên thần]], người quen',
              'Angel, [[vuon-uom-khoi-nghiep|vườn ươm]], quỹ chuyên pre-seed',
              '[[quy-dau-tu-mao-hiem|Quỹ đầu tư mạo hiểm]]',
            ],
            ['Quy mô', 'Vài trăm triệu tới một, hai tỷ', 'Một tới vài tỷ, có khi hơn chục tỷ', 'Vài chục tỷ trở lên'],
            ['Họ đánh giá chủ yếu', 'Con người', 'Con người và dấu hiệu nhu cầu', 'Chỉ số tăng trưởng'],
            [
              'Công cụ hay dùng',
              '[[safe-la-gi|SAFE]], cổ phần',
              'SAFE, cổ phần, [[trai-phieu-chuyen-doi|trái phiếu chuyển đổi]]',
              'Cổ phần, thường là [[co-phan-uu-dai|cổ phần ưu đãi]]',
            ],
          ],
        },
      },
      {
        h: 'Nhà đầu tư nhìn gì ở vòng này',
        li: [
          'Đội ngũ, nhiều hơn mọi thứ khác cộng lại. Chưa có số liệu để đánh giá, nên họ đặt cược vào người: ai đã từng làm việc này, ai hiểu khách hàng, đội có làm việc cùng nhau được lâu không.',
          'Bằng chứng có người cần. Chưa cần doanh thu, nhưng phải có thứ gì đó từ bên ngoài: [[thu-cam-ket-mua|thư cam kết mua]], [[trien-khai-thu|triển khai thử]], danh sách chờ, hoặc vài khách hàng đầu tiên. Xem thêm [[traction-la-gi|traction]].',
          'Vấn đề đủ lớn và đủ thật. Họ tìm dấu hiệu bạn đã nói chuyện trực tiếp với người đang chịu vấn đề đó, không phải suy đoán từ xa. [[quy-mo-thi-truong|Quy mô thị trường]] chỉ cần hợp lý, không cần con số khổng lồ.',
          'Kế hoạch dùng tiền rõ ràng. Gọi bao nhiêu, tiêu vào đâu, và đến cuối vòng sẽ chứng minh được điều gì.',
        ],
      },
      {
        h: 'Gọi bao nhiêu và định giá thế nào',
        p: [
          'Cách tính ngược thường đúng hơn cách đoán: liệt kê cột mốc cần đạt để gọi được vòng seed, tính chi phí mỗi tháng để đạt nó, nhân với 18 tháng rồi cộng thêm phần dự phòng.',
          'Ví dụ: đội 6 người, chi phí 180 triệu mỗi tháng. 18 tháng là 3,24 tỷ, cộng 15% dự phòng thành khoảng 3,7 tỷ. Nếu bán khoảng 15% công ty cho số tiền này, [[pre-money-post-money|định giá post-money]] vào khoảng 25 tỷ.',
          'Ở vòng pre-seed, phần bán ra thường nằm trong khoảng 10 tới 20%. Bán quá nhiều ngay từ vòng đầu khiến người sáng lập còn quá ít khi tới Series A, và nhà đầu tư vòng sau sẽ lo ngại điều đó.',
        ],
      },
      {
        h: 'Bảng cổ phần sau một vòng pre-seed điển hình',
        p: [
          'Tiếp ví dụ trên: hai người sáng lập chia 50/50, gọi 3,7 tỷ và bán 15% công ty (định giá post-money khoảng 25 tỷ). Nhà đầu tư yêu cầu thêm quỹ [[esop-la-gi|ESOP]] 8% tạo trước vòng.',
        ],
        bang: {
          chuThich: 'Ví dụ minh họa',
          cot: ['Cổ đông', 'Trước vòng', 'Sau vòng, không ESOP', 'Sau vòng, có ESOP 8%'],
          hang: [
            ['Người sáng lập A', '50%', '42,5%', '38,5%'],
            ['Người sáng lập B', '50%', '42,5%', '38,5%'],
            ['Quỹ ESOP', '', '', '8%'],
            ['Nhà đầu tư pre-seed', '', '15%', '15%'],
          ],
        },
        sau: [
          'Sau vòng đầu, nhóm sáng lập còn 77% đến 85%, nằm trong mức nhà đầu tư vòng sau thường muốn thấy. Mô phỏng tiếp vòng seed và Series A bằng [[/cong-cu/mo-phong-vong-goi-von|công cụ mô phỏng vòng gọi vốn]] để biết tới Series A mình còn bao nhiêu.',
        ],
      },
      {
        h: 'Lộ trình một vòng pre-seed',
        li: [
          'Chuẩn bị, 2 tới 4 tuần: [[pitch-deck-la-gi|pitch deck]], mô hình tài chính 18 tháng, [[bang-co-phan|bảng cổ phần]] sạch, hồ sơ pháp lý cơ bản.',
          'Lên danh sách, 1 tuần: 30 tới 50 nhà đầu tư thực sự đầu tư ở giai đoạn này, đúng ngành. Gửi cho quỹ chỉ làm Series A là mất thời gian của cả hai bên.',
          'Tiếp cận và gặp, 1 tới 2 tháng: ưu tiên [[warm-intro|giới thiệu qua người quen]], gặp dồn trong một khoảng ngắn để tạo nhịp.',
          'Đàm phán, [[due-diligence|thẩm định]] và ký, 1 tới 2 tháng. Tổng cộng thường từ 3 tới 6 tháng.',
        ],
      },
      {
        h: 'Hồ sơ cần có trước buổi gặp đầu tiên',
        li: [
          '[[pitch-deck-la-gi|Pitch deck]] 10 tới 12 slide, có slide gọi vốn ghi rõ số tiền, cách dùng và cột mốc. Tự rà bằng [[/cong-cu/pitch-deck-10-slide|công cụ kiểm tra pitch deck]].',
          '[[mo-hinh-tai-chinh|Mô hình tài chính]] 18 tới 24 tháng, giả định ghi rõ ràng.',
          '[[bang-co-phan|Bảng cổ phần]] hiện tại, khớp với đăng ký doanh nghiệp.',
          'Công ty cổ phần đã thành lập, người sáng lập đã ký [[vesting-la-gi|vesting]], sản phẩm và mã nguồn đã [[chuyen-giao-so-huu-tri-tue|chuyển giao về công ty]].',
          'Danh sách 30 tới 50 nhà đầu tư đúng giai đoạn, đúng ngành, kèm người có thể giới thiệu. Lọc nhanh bằng [[/cong-cu/quy-phu-hop|công cụ tìm nguồn vốn phù hợp]].',
          'Một [[data-room|data room]] cơ bản để gửi ngay khi nhà đầu tư quan tâm.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Bắt đầu quá muộn. Còn ba tháng [[runway-la-gi|runway]] mới gửi hồ sơ nghĩa là đàm phán ở thế yếu.',
          'Gửi hàng loạt thay vì có chọn lọc. Một hồ sơ viết riêng cho quỹ có [[khau-vi-dau-tu|khẩu vị]] phù hợp sẽ được đọc; thư gửi hàng loạt thì thường không.',
          'Định giá theo kỳ vọng thay vì theo mặt bằng. [[dinh-gia-qua-cao|Định giá quá cao]] ở vòng này khiến vòng seed rất khó.',
          'Hồ sơ pháp lý chưa sạch: người sáng lập chưa ký [[vesting-la-gi|vesting]], mã nguồn vẫn đứng tên cá nhân. Đây là những thứ làm chậm việc đóng vòng nhiều nhất.',
        ],
      },
      {
        h: 'Đến cuối vòng pre-seed cần chứng minh được gì',
        p: [
          'Tiền pre-seed là để mua bằng chứng cho vòng seed. Trước khi gọi, hãy viết ra cột mốc cụ thể mà nhà đầu tư seed sẽ muốn thấy với mô hình của bạn, ví dụ:',
        ],
        li: [
          'Phần mềm bán cho doanh nghiệp: có khách trả tiền định kỳ, doanh thu định kỳ tăng đều nhiều tháng liền, khách cũ ít bỏ đi.',
          'Sản phẩm tiêu dùng: người dùng quay lại sau tuần đầu và tháng đầu, chi phí có thêm một khách giảm dần.',
          'Sàn giao dịch: giao dịch lặp lại giữa người mua và người bán, không chỉ nhờ khuyến mãi.',
        ],
        sau: [
          'Con số cụ thể tùy ngành và thời điểm, nhưng nguyên tắc chung: cột mốc phải đo được, và số tiền gọi phải đủ để chạm tới nó với vài tháng dư. Xem [[cot-moc-goi-von|cột mốc gọi vốn]] và [[traction-la-gi|traction]].',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Chi phí 200 triệu mỗi tháng, cần 18 tháng, cộng 15% dự phòng. Nên gọi khoảng bao nhiêu? (Đáp án: 3,6 tỷ cộng 15%, khoảng 4,1 tỷ.)',
          'Gọi 4 tỷ và bán 16%. Định giá post-money là bao nhiêu? (Đáp án: 4 chia 0,16, tức 25 tỷ.)',
          'Bạn chưa có doanh thu nhưng có 3 thư cam kết mua từ doanh nghiệp. Đã đủ để gọi pre-seed chưa? (Đáp án: có thể, nếu đội ngũ mạnh và thư cam kết đến từ đúng khách hàng mục tiêu.)',
        ],
      },
    ],
    faq: [
      {
        q: 'Pre-seed khác seed thế nào?',
        a: 'Chủ yếu ở mức độ bằng chứng. Pre-seed thường chưa có doanh thu ổn định và nhà đầu tư đánh giá chủ yếu qua đội ngũ; seed thì đã có doanh thu đang tăng và họ đánh giá qua chỉ số. Quy mô vòng và định giá ở seed thường lớn hơn nhiều lần.',
      },
      {
        q: 'Vòng pre-seed ở Việt Nam thường gọi bao nhiêu tiền?',
        a: 'Thường từ một tới vài tỷ đồng, đôi khi hơn chục tỷ. Con số đúng cho bạn nên tính từ chi phí 12 tới 18 tháng để đạt cột mốc của vòng seed, không phải từ mức người khác đã gọi.',
      },
      {
        q: 'Có bắt buộc phải qua pre-seed trước seed không?',
        a: 'Không. Một số doanh nghiệp đi thẳng vào seed nếu đã có [[traction-la-gi|traction]] đủ mạnh, nhiều doanh nghiệp tự nuôi bằng doanh thu rồi mới gọi vốn. Ngược lại cũng có doanh nghiệp gọi hai vòng pre-seed.',
      },
      {
        q: 'Dùng SAFE hay bán cổ phần trực tiếp?',
        a: '[[safe-la-gi|SAFE]] nhanh và nhẹ hơn, phù hợp khi chưa muốn chốt định giá. Bán cổ phần trực tiếp rõ ràng hơn nhưng thủ tục nặng hơn. Quỹ trong nước thường thích cách thứ hai, nhà đầu tư quen thị trường Mỹ và Singapore thì quen SAFE.',
      },
      {
        q: 'Chưa có công ty thì gọi vốn pre-seed được không?',
        a: 'Khó. Nhà đầu tư cần một pháp nhân để rót tiền vào và nhận cổ phần. Nên thành lập công ty, ưu tiên [[cong-ty-co-phan-goi-von|công ty cổ phần]], trước khi bắt đầu nói chuyện nghiêm túc.',
      },
      {
        q: 'Gọi vốn pre-seed có cần doanh thu không?',
        a: 'Không bắt buộc. Nhưng cần một dạng bằng chứng từ bên ngoài: người dùng thử, thư cam kết mua, danh sách chờ hoặc vài khách trả tiền đầu tiên. Doanh thu nhỏ nhưng có thật luôn thuyết phục hơn dự báo lớn.',
      },
    ],
  },

  'pitch-deck-la-gi': {
    capNhat: '2026-10-12',
    lienQuan: [
      'one-pager-teaser',
      'elevator-pitch',
      'email-goi-nha-dau-tu',
      'traction-la-gi',
      'quy-mo-thi-truong',
      'so-tien-goi-von',
    ],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Pitch deck là bộ slide giới thiệu doanh nghiệp gửi cho nhà đầu tư, thường 10 tới 15 trang. Mục đích không phải giải thích hết mọi thứ, mà là khiến người đọc muốn gặp bạn.',
          'Deck gửi qua email thường chỉ được xem trong ba tới bốn phút. Mọi quyết định về độ dài và cách trình bày nên xuất phát từ con số đó: người đọc phải hiểu bạn làm gì, cho ai và vì sao đáng chú ý ngay trong vài slide đầu.',
        ],
      },
      {
        h: 'Pitch deck, slide deck, deck gọi vốn: có khác nhau không',
        p: [
          'Trong ngữ cảnh gọi vốn, ba cách gọi này chỉ cùng một thứ. "Slide deck" là cách gọi chung cho mọi bộ slide trình chiếu; khi nói tới nhà đầu tư, người ta thêm chữ "pitch" để chỉ bộ slide dùng để thuyết phục họ.',
          'Thứ thực sự khác nhau là mục đích sử dụng: bản gửi đọc một mình, bản trình bày trực tiếp, và [[one-pager-teaser|bản tóm tắt một trang]] để mở lời. Phần cuối trang này nói rõ từng loại.',
        ],
      },
      {
        h: 'Mười slide cốt lõi và câu hỏi mỗi slide phải trả lời',
        bang: {
          cot: ['Slide', 'Người đọc cần biết'],
          hang: [
            [
              '1. Vấn đề',
              'Ai đang gặp chuyện gì, và họ đang xử lý tạm bằng cách nào?',
            ],
            [
              '2. Giải pháp',
              'Bạn làm gì khác, và vì sao cách đó tốt hơn rõ rệt?',
            ],
            [
              '3. Vì sao là lúc này',
              'Điều gì vừa thay đổi khiến việc này giờ mới làm được?',
            ],
            [
              '4. Thị trường',
              '[[quy-mo-thi-truong|Thị trường]] có đủ lớn, ước lượng từ dưới lên có hợp lý không?',
            ],
            [
              '5. Bằng chứng',
              'Ai đã dùng, trả tiền, quay lại? Đây là slide [[traction-la-gi|traction]].',
            ],
            [
              '6. Cách kiếm tiền',
              'Thu tiền ai, bao nhiêu, [[kinh-te-don-vi|kinh tế đơn vị]] có lãi không?',
            ],
            [
              '7. Cạnh tranh',
              'Khách hàng đang dùng gì thay thế, và vì sao họ sẽ chuyển?',
            ],
            [
              '8. Đội ngũ',
              'Vì sao chính những người này làm được việc này?',
            ],
            [
              '9. Tài chính',
              'Doanh thu và chi phí 18 tới 36 tháng tới, giả định chính là gì?',
            ],
            [
              '10. Gọi vốn',
              '[[so-tien-goi-von|Gọi bao nhiêu]], dùng vào đâu, đạt cột mốc gì?',
            ],
          ],
        },
        sau: [
          'Thứ tự có thể đổi. Đội ngũ mạnh thì đưa lên sớm; đã có doanh thu tốt thì đưa slide bằng chứng lên ngay sau giải pháp. Nhưng thiếu slide nào trong số này đều khiến người đọc phải tự đoán, và họ thường không đoán.',
        ],
      },
      {
        h: 'Ba loại deck cần phân biệt',
        li: [
          'Deck gửi email: phải tự giải thích được vì bạn không có mặt. Chữ nhiều hơn một chút là chấp nhận được. Gửi dạng PDF, dung lượng nhỏ, mở được trên điện thoại.',
          'Deck trình bày: ít chữ, mỗi slide một ý, để người nghe nhìn bạn thay vì đọc màn hình.',
          '[[one-pager-teaser|One-pager]]: một trang tóm tắt dùng để mở lời qua người giới thiệu, trước khi gửi deck đầy đủ.',
        ],
        sau: [
          'Nếu chỉ làm được một bản, hãy ưu tiên bản gửi email. Đó là bản được đọc nhiều nhất, và thường là bản quyết định có cuộc gặp hay không.',
        ],
      },
      {
        h: 'Nhà đầu tư đọc deck theo thứ tự nào',
        p: [
          'Phần lớn nhà đầu tư không đọc từ slide 1 tới slide cuối. Họ lướt để trả lời ba câu: đây là gì, có ai đang dùng không, và người làm là ai. Rồi họ nhảy tới slide gọi vốn để biết bạn cần gì. Nếu ba câu đó có câu trả lời rõ trong vài phút đầu, họ mới quay lại đọc kỹ.',
          'Vì vậy câu một dòng mô tả công ty ở slide đầu, slide bằng chứng và slide đội ngũ thường quyết định nhiều hơn mọi slide khác cộng lại. Bài [[/playbook/quy-nhin-gi-trong-5-phut-dau-pitch-deck|Quỹ nhìn gì trong 5 phút đầu của pitch deck]] đi sâu hơn vào phần này.',
        ],
      },
      {
        h: 'Slide gọi vốn: một ví dụ viết tốt',
        p: [
          'Tiêu đề: "Gọi 4 tỷ để đạt 1,2 tỷ doanh thu định kỳ năm trong 18 tháng".',
        ],
        li: [
          'Số tiền: 4 tỷ, dùng SAFE hoặc cổ phần ưu đãi.',
          '[[su-dung-von|Dùng vào đâu]]: 55% đội sản phẩm (tuyển 3 kỹ sư), 30% bán hàng và marketing, 15% vận hành và dự phòng.',
          '[[cot-moc-goi-von|Cột mốc]] cuối vòng: 120 khách trả phí, doanh thu định kỳ 100 triệu mỗi tháng, tỷ lệ khách rời bỏ dưới 3% mỗi tháng.',
          '[[runway-la-gi|Runway]]: 18 tháng, đủ thời gian gọi vòng seed khi còn 6 tháng tiền.',
        ],
        sau: [
          'Người đọc biết ngay bạn cần gì, tiền đi đâu, và cuối vòng họ sẽ được thấy gì. So với một slide chỉ ghi "Gọi 3 đến 5 tỷ để mở rộng thị trường", khác biệt rất lớn.',
        ],
      },
      {
        h: 'Sai lầm phổ biến nhất',
        li: [
          'Điền đúng tiêu đề nhưng sai nội dung. Slide cạnh tranh ghi "chúng tôi không có đối thủ" là ví dụ kinh điển: khách hàng luôn đang giải quyết vấn đề bằng cách nào đó, kể cả bằng Excel.',
          'Tiêu đề slide chỉ là nhãn ("Thị trường", "Đội ngũ"). Tiêu đề tốt là một câu kết luận, ví dụ "Khách quay lại mua lần hai sau trung bình 3 tuần". Người đọc lướt chỉ đọc tiêu đề.',
          'Slide gọi vốn để trống hoặc ghi một khoảng rộng. Nhà đầu tư cần biết bạn muốn gì để quyết định có nói tiếp hay không.',
          'Quá dài. Trên 20 slide thì gần như chắc chắn có phần không ai đọc. Giữ 10 tới 12 slide chính, đẩy phần còn lại xuống phụ lục.',
          'Số liệu không khớp nhau giữa các slide, ví dụ số khách ở slide bằng chứng khác với slide tài chính. Đây là lỗi làm mất niềm tin nhanh nhất.',
        ],
      },
      {
        h: 'Rà deck trước khi gửi',
        li: [
          'Đọc riêng các tiêu đề slide từ đầu tới cuối: có kể được câu chuyện không?',
          'Đưa deck cho một người ngoài ngành đọc trong 3 phút, hỏi họ bạn làm gì cho ai. Họ không trả lời được thì nhà đầu tư cũng khó.',
          'Mọi con số giữa các slide có khớp nhau và khớp [[mo-hinh-tai-chinh|mô hình tài chính]] không.',
          'Slide gọi vốn ghi một con số cụ thể, cách dùng và cột mốc.',
          'Tệp PDF dưới 10 MB, mở tốt trên điện thoại, tên tệp có tên công ty và tháng.',
          'Có thông tin liên hệ ở slide cuối.',
        ],
        sau: [
          'Chấm deck theo khung 10 slide bằng [[/cong-cu/pitch-deck-10-slide|công cụ kiểm tra pitch deck]], hoặc [[/nhan-xet-deck|gửi deck để được nhận xét]] trước khi gửi nhà đầu tư.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Tiêu đề "Thị trường" hay "Thị trường bán lẻ dược 70.000 tỷ, 60% vẫn đặt hàng qua điện thoại" tốt hơn? (Đáp án: câu thứ hai, vì nó là kết luận người đọc mang đi được.)',
          'Deck của bạn 24 slide. Nên làm gì? (Đáp án: giữ 10 tới 12 slide chính, đưa phần còn lại xuống phụ lục.)',
          'Slide bằng chứng ghi 500 người dùng, slide tài chính tính từ 350 người dùng trả phí. Có vấn đề gì không? (Đáp án: có thể không sai, nhưng phải ghi rõ hai con số khác nhau ở đâu, nếu không người đọc sẽ nghĩ số liệu không khớp.)',
        ],
      },
    ],
    faq: [
      {
        q: 'Slide deck là gì, có giống pitch deck không?',
        a: 'Slide deck là cách gọi chung cho một bộ slide trình chiếu. Khi dùng để gọi vốn, nó chính là pitch deck. Hai cách gọi chỉ cùng một tài liệu.',
      },
      {
        q: 'Pitch deck nên có bao nhiêu slide?',
        a: 'Khoảng 10 tới 15 slide cho phần chính. Nhiều hơn thì đưa xuống phụ lục. Mười slide cốt lõi ở bảng trên là khung đủ dùng cho vòng angel và [[vong-pre-seed|pre-seed]].',
      },
      {
        q: 'Có nên gửi deck ngay trong email đầu tiên không?',
        a: 'Thường là có. Nhà đầu tư nhận rất nhiều thư và cần đủ thông tin để quyết định có trả lời hay không. Giữ deck lại để "chỉ chia sẻ khi gặp" thường khiến cuộc gặp không bao giờ diễn ra. Xem thêm [[email-goi-nha-dau-tu|cách viết email đầu tiên]].',
      },
      {
        q: 'Deck có cần thiết kế đẹp không?',
        a: 'Cần rõ ràng hơn là cần đẹp. Trình bày cẩu thả gây ấn tượng xấu, nhưng thiết kế cầu kỳ không bù được nội dung yếu.',
      },
      {
        q: 'Pitch deck viết bằng tiếng Việt hay tiếng Anh?',
        a: 'Theo người đọc. Quỹ và angel trong nước đọc tiếng Việt thoải mái; quỹ khu vực thường cần tiếng Anh. Nhiều startup chuẩn bị cả hai bản, nhưng hãy viết xong một bản thật tốt trước rồi mới dịch.',
      },
      {
        q: 'Có nên ghi định giá mong muốn trong pitch deck không?',
        a: 'Thường là không cần ở deck gửi đi lần đầu. Ghi số tiền gọi và cách dùng là đủ; định giá để dành cho cuộc nói chuyện khi nhà đầu tư đã quan tâm. Nếu dùng SAFE, có thể ghi mức trần dự kiến khi đã chắc chắn.',
      },
    ],
  },

  'vesting-la-gi': {
    capNhat: '2026-10-12',
    lienQuan: [
      'cliff-vesting',
      'vesting-tang-toc',
      'good-leaver-bad-leaver',
      'co-phan-chet',
      'thoa-thuan-dong-sang-lap',
      'esop-la-gi',
    ],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Vesting là cơ chế nhận cổ phần dần theo thời gian gắn bó với công ty, thay vì nhận hết ngay từ ngày đầu. Ai rời đi sớm chỉ giữ phần đã "về tay", phần còn lại quay về công ty hoặc các cổ đông còn ở lại.',
          'Thông lệ phổ biến nhất là 4 năm, có mốc chờ ([[cliff-vesting|cliff]]) 12 tháng: rời đi trước 12 tháng thì không giữ gì, đủ 12 tháng thì nhận ngay 25%, sau đó nhận đều mỗi tháng cho tới hết năm thứ tư.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Công ty có 1.000.000 cổ phần. Một người đồng sáng lập được chia 40%, tức 400.000 cổ phần, theo lịch 4 năm, cliff 12 tháng. Sau cliff, mỗi tháng người này nhận thêm khoảng 8.333 cổ phần (400.000 chia 48 tháng).',
        ],
        bang: {
          chuThich: 'Số cổ phần đã vesting nếu rời đi ở từng thời điểm',
          cot: ['Rời đi sau', 'Đã vesting', 'Tỷ lệ trên phần được chia'],
          hang: [
            ['6 tháng', '0', '0%'],
            ['12 tháng', '100.000', '25%'],
            ['24 tháng', '200.000', '50%'],
            ['30 tháng', '250.000', '62,5%'],
            ['48 tháng', '400.000', '100%'],
          ],
        },
        sau: [
          'Phần chưa vesting (ví dụ 150.000 cổ phần nếu rời đi sau 30 tháng) thường được công ty mua lại theo giá thấp đã thoả thuận từ trước, rồi dùng để tuyển người thay thế hoặc đưa vào [[esop-la-gi|ESOP]].',
        ],
      },
      {
        h: 'Vì sao nhà đầu tư luôn hỏi',
        p: [
          'Không có vesting, một người đồng sáng lập rời đi sau ba tháng vẫn giữ nguyên 30 hay 40% công ty. Phần đó trở thành [[co-phan-chet|cổ phần chết]]: thuộc về người không còn đóng góp, nhưng vẫn bị tính khi chia mọi thứ về sau.',
          'Với nhà đầu tư, đây là rủi ro họ không kiểm soát được, nên vesting cho toàn bộ người sáng lập gần như là điều kiện bắt buộc trước khi xuống tiền. Họ cũng hay hỏi vesting có [[vesting-tang-toc|tăng tốc]] khi công ty được bán hay không, và xử lý thế nào giữa người ra đi tốt và ra đi xấu ([[good-leaver-bad-leaver|good leaver, bad leaver]]).',
        ],
      },
      {
        h: 'Các biến thể hay gặp',
        bang: {
          cot: ['Kiểu', 'Cách hoạt động', 'Khi nào dùng'],
          hang: [
            [
              '4 năm, cliff 12 tháng',
              'Đủ 12 tháng nhận 25%, sau đó nhận đều hàng tháng',
              'Mặc định cho người sáng lập và nhân sự chủ chốt',
            ],
            [
              'Ghi nhận thời gian đã làm',
              'Người đã làm 2 năm trước khi ký được tính là đã vesting một phần',
              'Công ty đã chạy một thời gian rồi mới ký vesting',
            ],
            [
              'Vesting theo cột mốc',
              'Nhận cổ phần khi đạt mục tiêu cụ thể (ra mắt sản phẩm, đạt doanh thu)',
              'Cố vấn, đối tác, người góp sức theo dự án',
            ],
            [
              '[[vesting-tang-toc|Tăng tốc]] một điều kiện',
              'Công ty được bán thì toàn bộ phần chưa vesting về tay ngay',
              'Ít gặp, bên mua thường không thích',
            ],
            [
              'Tăng tốc hai điều kiện',
              'Công ty được bán và người đó bị cho nghỉ việc thì mới tăng tốc',
              'Phổ biến hơn, cân bằng giữa người sáng lập và bên mua',
            ],
          ],
        },
      },
      {
        h: 'Người ra đi tốt, người ra đi xấu',
        p: [
          'Không phải ai rời đi cũng giống nhau. Thỏa thuận thường chia hai trường hợp ([[good-leaver-bad-leaver|good leaver, bad leaver]]):',
        ],
        li: [
          'Ra đi tốt: nghỉ vì sức khỏe, gia đình, hoặc bị cho nghỉ không do lỗi. Thường giữ phần đã vesting; phần chưa vesting được công ty mua lại ở giá đã góp ban đầu.',
          'Ra đi xấu: vi phạm nghiêm trọng, làm cho đối thủ, tiết lộ bí mật kinh doanh. Thỏa thuận có thể cho công ty mua lại cả phần đã vesting ở giá thấp.',
        ],
        sau: [
          'Định nghĩa từng trường hợp phải viết rõ từ đầu. Một điều khoản "ra đi xấu" quá rộng (ví dụ nghỉ việc tự nguyện cũng tính là xấu) khiến người giỏi ngại ký, và có thể khó thực thi theo luật Việt Nam.',
        ],
      },
      {
        h: 'Làm vesting ở công ty Việt Nam',
        p: [
          'Luật Doanh nghiệp không có khái niệm vesting, nên nó được thực hiện bằng hợp đồng: thường là cấp đủ cổ phần ngay từ đầu, kèm thoả thuận cho công ty hoặc các cổ đông khác quyền mua lại phần chưa vesting khi một người rời đi, ở mức giá ghi sẵn.',
          'Điều khoản này nên nằm trong [[thoa-thuan-dong-sang-lap|thoả thuận đồng sáng lập]] và được phản ánh vào [[dieu-le-cong-ty|điều lệ]], vì một thoả thuận riêng mâu thuẫn với điều lệ có thể khó thực thi. Cách thiết kế cụ thể nên làm với luật sư.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chờ tới lúc gọi vốn mới ký. Khi đó việc ký vesting thành cuộc đàm phán giữa những người vốn là bạn, có người sẽ hỏi "vì sao tôi phải chịu".',
          'Tính lại từ đầu cho người đã làm nhiều năm. Thời gian đã đóng góp nên được ghi nhận là đã vesting một phần, thay vì bắt chờ lại 4 năm.',
          'Chỉ áp dụng cho người khác, không áp dụng cho CEO. Nhà đầu tư muốn thấy mọi người sáng lập cùng chịu một luật.',
          'Không ghi rõ giá mua lại phần chưa vesting. Tới lúc có người ra đi mới thương lượng giá là lúc khó thương lượng nhất.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Bạn được chia 480.000 cổ phần, 4 năm, cliff 12 tháng. Rời đi sau 10 tháng, bạn giữ bao nhiêu? (Đáp án: 0.)',
          'Cùng lịch đó, rời đi sau 18 tháng? (Đáp án: 120.000 sau cliff, cộng 6 tháng nhân 10.000, tức 180.000 cổ phần.)',
          'Ba người sáng lập đã làm cùng nhau 2 năm, giờ mới ký vesting 4 năm. Cách công bằng là gì? (Đáp án: ghi nhận 2 năm đã làm, tức đã vesting khoảng 50%, phần còn lại vesting tiếp trong 2 năm.)',
        ],
        sau: [
          'Đặt vesting cùng lúc với việc chia cổ phần giữa các đồng sáng lập bằng [[/cong-cu/chia-co-phan-dong-sang-lap|công cụ chia cổ phần đồng sáng lập]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Vesting là gì, giải thích đơn giản?',
        a: 'Là nhận cổ phần dần dần theo thời gian làm việc. Làm đủ một năm mới bắt đầu có, sau đó mỗi tháng có thêm một phần, đủ bốn năm thì nhận hết.',
      },
      {
        q: 'Cliff là gì?',
        a: '[[cliff-vesting|Cliff]] là mốc chờ đầu tiên, thường 12 tháng. Trước mốc này chưa nhận được cổ phần nào; tới mốc thì nhận một lần phần tương ứng với thời gian đã làm, thường là 25%.',
      },
      {
        q: 'Người sáng lập có cần vesting không, công ty là của mình mà?',
        a: 'Có. Vesting bảo vệ chính những người ở lại. Nếu bạn là người ở lại khi một đồng sáng lập bỏ đi, bạn sẽ muốn phần cổ phần của họ quay về công ty.',
      },
      {
        q: 'Vesting áp dụng cho nhân viên như thế nào?',
        a: 'Tương tự, thông qua [[esop-la-gi|ESOP]]: nhân viên được cấp quyền mua cổ phần và quyền này vesting theo lịch, thường cũng 4 năm với cliff 12 tháng.',
      },
      {
        q: 'Cổ phần chưa vesting có được nhận cổ tức và biểu quyết không?',
        a: 'Với cách làm phổ biến ở Việt Nam (cấp đủ cổ phần ngay, kèm quyền mua lại phần chưa vesting), người giữ vẫn là cổ đông nên về nguyên tắc có quyền biểu quyết và nhận cổ tức. Nếu muốn hạn chế, phải viết rõ trong thỏa thuận và điều lệ; hỏi luật sư cách làm cho chặt.',
      },
    ],
  },

  'bang-co-phan': {
    capNhat: '2026-10-12',
    lienQuan: ['pha-loang-co-phan', 'pha-loang-toan-phan', 'esop-la-gi', 'pre-money-post-money', 'safe-la-gi', 'von-dieu-le'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Bảng cổ phần (cap table, viết tắt của capitalization table) là bảng ghi ai đang sở hữu bao nhiêu cổ phần và bao nhiêu phần trăm công ty. Nó là nguồn sự thật duy nhất về quyền sở hữu, và là tài liệu nhà đầu tư hỏi tới rất sớm, thường ngay trong cuộc gặp thứ hai.',
          'Một bảng cổ phần đúng nghĩa không chỉ có cổ phần đã phát hành, mà còn cả những thứ sẽ trở thành cổ phần: quỹ [[esop-la-gi|ESOP]], các khoản [[safe-la-gi|SAFE]] hoặc [[trai-phieu-chuyen-doi|trái phiếu chuyển đổi]] đang chờ chuyển đổi.',
        ],
      },
      {
        h: 'Một bảng cổ phần trước và sau vòng gọi vốn',
        p: [
          'Công ty có 1.000.000 cổ phần tính trên cơ sở [[pha-loang-toan-phan|pha loãng toàn phần]]. Nhà đầu tư mới bỏ 6 tỷ với [[pre-money-post-money|định giá pre-money]] 24 tỷ. Giá mỗi cổ phần là 24 tỷ chia 1.000.000, tức 24.000 đồng. Số cổ phần mới phát hành là 6 tỷ chia 24.000, tức 250.000 cổ phần.',
        ],
        bang: {
          cot: ['Cổ đông', 'Trước vòng', 'Tỷ lệ', 'Sau vòng', 'Tỷ lệ'],
          hang: [
            ['Người sáng lập A', '500.000', '50%', '500.000', '40%'],
            ['Người sáng lập B', '300.000', '30%', '300.000', '24%'],
            ['Quỹ ESOP', '100.000', '10%', '100.000', '8%'],
            ['Angel vòng trước', '100.000', '10%', '100.000', '8%'],
            ['Nhà đầu tư mới', '', '', '250.000', '20%'],
            ['Tổng', '1.000.000', '100%', '1.250.000', '100%'],
          ],
        },
        sau: [
          'Số cổ phần của người sáng lập không đổi, nhưng tỷ lệ giảm từ 50% xuống 40%. Đó là [[pha-loang-co-phan|pha loãng]]. Đổi lại, phần 40% giờ nằm trong một công ty định giá 30 tỷ thay vì 24 tỷ.',
        ],
      },
      {
        h: 'Bảng đầy đủ gồm những gì',
        li: [
          'Danh sách cổ đông, loại cổ phần ([[co-phan-pho-thong|phổ thông]] hay [[co-phan-uu-dai|ưu đãi]]), số lượng, ngày cấp.',
          'Lịch [[vesting-la-gi|vesting]] của từng người sáng lập và nhân sự.',
          'Quỹ ESOP: tổng quy mô, đã cấp bao nhiêu, còn lại bao nhiêu.',
          'Các khoản SAFE và trái phiếu chuyển đổi: số tiền, [[valuation-cap|valuation cap]], [[discount-safe|discount]], để mô phỏng khi chuyển đổi.',
          'Một bản tính trên cơ sở pha loãng toàn phần, vì nhà đầu tư luôn nhìn theo cách này.',
        ],
      },
      {
        h: 'Bảng cổ phần và vốn điều lệ: hai con số khác nhau',
        p: [
          'Trong ví dụ trên, công ty có 1.000.000 cổ phần. Nếu mệnh giá mỗi cổ phần là 10.000 đồng thì [[von-dieu-le|vốn điều lệ]] là 10 tỷ. Sau vòng, công ty có 1.250.000 cổ phần, vốn điều lệ tăng lên 12,5 tỷ, trong khi định giá post-money là 30 tỷ.',
          'Vốn điều lệ tính theo mệnh giá và là con số pháp lý; định giá là con số thương mại do hai bên thỏa thuận. Nhà đầu tư mua mỗi cổ phần 24.000 đồng; phần chênh so với mệnh giá được ghi nhận là thặng dư vốn cổ phần. Bảng cổ phần nên ghi cả số cổ phần (để khớp với sổ đăng ký cổ đông) lẫn tỷ lệ (để đàm phán). Xem thêm [[pre-money-post-money|định giá pre-money và post-money]].',
        ],
      },
      {
        h: 'Tự dựng bảng cổ phần trong một buổi',
        li: [
          'Bước 1: lấy sổ đăng ký cổ đông và giấy chứng nhận đăng ký doanh nghiệp, liệt kê từng cổ đông, loại cổ phần, số lượng, ngày góp.',
          'Bước 2: đối chiếu với chứng từ góp vốn. Ai chưa góp đủ phần đã đăng ký thì ghi chú.',
          'Bước 3: thêm quỹ ESOP (tổng, đã cấp, còn lại) và lịch vesting của từng người.',
          'Bước 4: thêm từng khoản SAFE và trái phiếu chuyển đổi với số tiền, mức trần, chiết khấu.',
          'Bước 5: tính tỷ lệ theo hai cách, trên cổ phần đã phát hành và trên cơ sở [[pha-loang-toan-phan|pha loãng toàn phần]].',
          'Bước 6: ghi ngày cập nhật, lưu một bản duy nhất, và mô phỏng vòng tiếp theo bằng [[/cong-cu/bang-co-phan-va-pha-loang|công cụ bảng cổ phần và pha loãng]].',
        ],
      },
      {
        h: 'Nhà đầu tư đọc bảng cổ phần để tìm gì',
        li: [
          'Người sáng lập còn đủ cổ phần để có động lực làm tiếp nhiều năm không.',
          '[[co-phan-chet|Cổ phần chết]]: phần lớn nằm trong tay người đã rời đi hoặc không còn đóng góp.',
          'Quá nhiều cổ đông nhỏ lẻ, mỗi người vài phần trăm, khiến mọi quyết định và thủ tục đều chậm.',
          'Còn chỗ cho ESOP để tuyển người giỏi không.',
          'Các khoản sẽ chuyển đổi: có bao nhiêu phần trăm "đã hứa" mà chưa hiện trên sổ đăng ký cổ đông.',
          'Bảng có khớp với hồ sơ đăng ký doanh nghiệp không. Không khớp là câu hỏi đầu tiên khi [[due-diligence|thẩm định]].',
        ],
      },
      {
        h: 'Vì sao dựng lại từ trí nhớ rất khó',
        p: [
          'Những thoả thuận miệng lúc mới bắt đầu ("anh làm phần kỹ thuật thì lấy 20%"), các lần góp thêm tiền không ghi giấy, một khoản SAFE ký từ năm trước: tất cả đều phải được đối chiếu với giấy tờ khi [[due-diligence|thẩm định]]. Chênh lệch giữa bảng của bạn và hồ sơ đăng ký doanh nghiệp là thứ bị hỏi đầu tiên.',
          'Ở Việt Nam còn một lớp nữa: bảng cổ phần nội bộ phải khớp với sổ đăng ký cổ đông và [[von-dieu-le|vốn điều lệ]] đã đăng ký. Thoả thuận vesting hay ESOP chưa thực hiện thủ tục thì chỉ nằm trên bảng nội bộ, và cần ghi chú rõ.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Quên các khoản SAFE chưa chuyển đổi. Khi chuyển đổi, chúng có thể lấy đi vài phần trăm mà người sáng lập không lường trước.',
          'Chỉ tính cổ phần đã phát hành, không tính ESOP. Tỷ lệ của bạn trên bảng như vậy trông cao hơn thực tế.',
          'Mỗi người giữ một phiên bản riêng. Nên có một file duy nhất, ghi ngày cập nhật, ai sửa gì.',
          'Chia quá nhiều cho cố vấn hoặc người góp sức ngắn hạn ngay từ đầu, không kèm vesting.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Công ty có 2.000.000 cổ phần, bạn giữ 800.000. Bạn sở hữu bao nhiêu phần trăm? (Đáp án: 40%.)',
          'Công ty phát hành thêm 500.000 cổ phần cho nhà đầu tư. Tỷ lệ của bạn còn bao nhiêu? (Đáp án: 800.000 chia 2.500.000, tức 32%.)',
          'Bảng của bạn ghi 40% nhưng có quỹ ESOP 10% chưa cấp và một SAFE sẽ chuyển thành 5%. Trên cơ sở pha loãng toàn phần, bạn còn khoảng bao nhiêu? (Đáp án: khoảng 40% nhân 0,85, tức 34%, tùy cách quỹ và SAFE được tính.)',
        ],
      },
    ],
    faq: [
      {
        q: 'Cap table là gì?',
        a: 'Là bảng cổ phần: bảng ghi ai sở hữu bao nhiêu cổ phần và bao nhiêu phần trăm công ty, gồm cả quỹ ESOP và các khoản sẽ chuyển thành cổ phần như SAFE.',
      },
      {
        q: 'Cap table khác sổ đăng ký cổ đông thế nào?',
        a: 'Sổ đăng ký cổ đông là hồ sơ pháp lý bắt buộc, ghi cổ phần đã phát hành. Cap table là công cụ quản trị, có thêm ESOP, vesting và các khoản chuyển đổi chưa thành cổ phần. Hai thứ phải khớp nhau ở phần cổ phần đã phát hành.',
      },
      {
        q: 'Làm cap table bằng Excel được không?',
        a: 'Được, ở giai đoạn sớm hầu hết startup làm bằng bảng tính. Quan trọng là một bản duy nhất, có công thức pha loãng đúng và được cập nhật mỗi lần có thay đổi.',
      },
      {
        q: 'Sau mỗi vòng gọi vốn, người sáng lập thường bị pha loãng bao nhiêu?',
        a: 'Mỗi vòng thường bán ra khoảng 10 tới 25% công ty, cộng thêm phần ESOP mở rộng nếu có. Mô phỏng trước bằng [[/cong-cu/mo-phong-vong-goi-von|công cụ mô phỏng vòng gọi vốn]] để thấy tỷ lệ còn lại qua nhiều vòng.',
      },
      {
        q: 'Ai chịu trách nhiệm giữ bảng cổ phần?',
        a: 'Thường là CEO hoặc người phụ trách tài chính. Quan trọng là chỉ một người được sửa, mọi thay đổi có ngày và lý do, và bảng được đối chiếu với sổ đăng ký cổ đông mỗi khi có giao dịch.',
      },
    ],
  },

  'goi-von-la-gi': {
    capNhat: '2026-10-12',
    lienQuan: [
      'vong-pre-seed',
      'vong-seed',
      'nha-dau-tu-thien-than',
      'quy-dau-tu-mao-hiem',
      'tai-tro-khong-hoan-lai',
      'bootstrapping',
    ],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Gọi vốn là việc huy động tiền từ bên ngoài để phát triển doanh nghiệp. Với startup, cách phổ biến nhất là bán một phần quyền sở hữu công ty cho nhà đầu tư, đổi lấy tiền để tăng trưởng nhanh hơn mức doanh thu tự có cho phép.',
          'Nhà đầu tư không cho vay. Họ chấp nhận mất trắng nếu công ty thất bại, đổi lại kỳ vọng phần sở hữu đó tăng giá trị nhiều lần khi công ty lớn lên, được bán hoặc niêm yết ([[exit-la-gi|exit]]).',
        ],
      },
      {
        h: 'Các nguồn vốn và khác biệt của chúng',
        bang: {
          cot: ['Nguồn', 'Bạn đổi lấy gì', 'Hợp với ai'],
          hang: [
            [
              'Bán cổ phần (vốn chủ sở hữu)',
              'Một phần quyền sở hữu, đôi khi một phần quyền quyết định',
              'Startup cần tăng trưởng nhanh, chưa có lãi',
            ],
            [
              'Vay ngân hàng',
              'Trả gốc và lãi, thường cần tài sản bảo đảm',
              'Doanh nghiệp có dòng tiền ổn định',
            ],
            [
              '[[tai-tro-khong-hoan-lai|Tài trợ không hoàn lại]]',
              'Thời gian làm hồ sơ và báo cáo theo yêu cầu',
              'Dự án đổi mới sáng tạo, công nghệ, tác động xã hội',
            ],
            [
              'Doanh thu ([[bootstrapping|bootstrapping]])',
              'Tăng trưởng chậm hơn',
              'Mô hình có lãi sớm',
            ],
          ],
        },
        sau: [
          'Phần lớn nội dung trên fundraising.vn nói về nguồn đầu tiên, vì đó là nguồn founder lần đầu thường ít hiểu nhất.',
        ],
      },
      {
        h: 'Bán cổ phần nghĩa là gì, nói bằng số',
        p: [
          'Công ty của bạn được nhà đầu tư định giá 24 tỷ trước khi nhận tiền ([[pre-money-post-money|định giá pre-money]]). Họ đầu tư 6 tỷ. Công ty phát hành thêm cổ phần mới cho họ, và sau vòng này họ sở hữu 6 chia 30, tức 20% công ty.',
          'Hai điều founder lần đầu hay hiểu nhầm. Thứ nhất, 6 tỷ đi vào tài khoản công ty để chi cho kế hoạch kinh doanh, không phải vào túi người sáng lập. Thứ hai, người sáng lập không bán cổ phần của mình; số cổ phần của họ giữ nguyên, chỉ tỷ lệ giảm từ 100% xuống 80% vì tổng số cổ phần tăng lên. Hiện tượng đó gọi là [[pha-loang-co-phan|pha loãng]].',
          'Trường hợp người sáng lập bán lại một phần cổ phần đang có của mình cho nhà đầu tư thì gọi là bán cổ phần thứ cấp. Tiền khi đó vào túi người bán, công ty không nhận được đồng nào. Ở vòng sớm, việc này hiếm và thường khiến nhà đầu tư e ngại.',
        ],
      },
      {
        h: 'Nhà đầu tư kiếm tiền thế nào, và vì sao bạn cần hiểu điều đó',
        p: [
          'Một [[quy-dau-tu-mao-hiem|quỹ đầu tư mạo hiểm]] đầu tư vào nhiều công ty, biết trước phần lớn sẽ thất bại hoặc chỉ hòa vốn. Toàn bộ lợi nhuận của quỹ thường đến từ một vài công ty tăng giá trị hàng chục lần. Vì vậy, với mỗi công ty, câu hỏi thật của họ là: nếu mọi thứ suôn sẻ, công ty này có thể lớn tới mức trả lại cả quỹ không?',
          'Điều này giải thích nhiều câu hỏi tưởng như khó chịu: vì sao họ hỏi về quy mô thị trường, về tốc độ tăng trưởng, về việc sau này ai sẽ mua công ty ([[exit-la-gi|exit]]). Một doanh nghiệp có lãi đều đặn nhưng khó lớn gấp nhiều lần vẫn là doanh nghiệp tốt, chỉ là không hợp với quỹ mạo hiểm. Với doanh nghiệp đó, vốn vay, tài trợ hoặc tự nuôi bằng doanh thu thường hợp hơn.',
          '[[nha-dau-tu-thien-than|Nhà đầu tư thiên thần]] dùng tiền của chính mình nên linh hoạt hơn: họ có thể đầu tư vì tin người sáng lập, vì hiểu ngành, hoặc vì muốn giúp. Nhưng họ cũng kỳ vọng phần sở hữu tăng giá trị theo thời gian.',
        ],
      },
      {
        h: 'Ai đầu tư vào startup ở Việt Nam',
        li: [
          'Nhà đầu tư thiên thần và mạng lưới thiên thần: thường là người đầu tiên bỏ tiền, séc từ vài trăm triệu tới vài tỷ.',
          '[[vuon-uom-khoi-nghiep|Vườn ươm và chương trình tăng tốc]]: đổi một khoản tiền nhỏ hoặc hỗ trợ lấy một phần cổ phần, kèm cố vấn và mạng lưới.',
          'Quỹ đầu tư mạo hiểm trong nước và khu vực: từ vòng pre-seed hoặc seed trở lên, mỗi quỹ có [[khau-vi-dau-tu|khẩu vị]] riêng về ngành và giai đoạn.',
          'Quỹ của tập đoàn ([[cvc-quy-doanh-nghiep|CVC]]): đầu tư khi startup liên quan tới chiến lược kinh doanh của họ.',
          'Chương trình tài trợ và hỗ trợ của nhà nước, tổ chức quốc tế: không lấy cổ phần nhưng yêu cầu hồ sơ và báo cáo.',
        ],
        sau: [
          'Danh mục các quỹ và chương trình đang hoạt động, kèm khẩu vị đầu tư, có ở trang [[/nguon-von|nguồn vốn]]. Muốn biết nguồn nào hợp với mình, dùng [[/cong-cu/quy-phu-hop|công cụ tìm nguồn vốn phù hợp]].',
        ],
      },
      {
        h: 'Khi nào nên gọi vốn',
        li: [
          'Khi tiền là thứ thực sự đang giới hạn tốc độ, không phải sản phẩm hay việc hiểu khách hàng. Đã thấy rõ bỏ thêm tiền vào đâu thì ra thêm kết quả.',
          'Khi đã có một ít bằng chứng có người cần. Gọi vốn chỉ bằng ý tưởng rất khó, kể cả ở vòng angel.',
          'Khi còn đủ [[runway-la-gi|runway]], ít nhất 9 tới 12 tháng. Một vòng thường mất 3 tới 6 tháng.',
        ],
      },
      {
        h: 'Khi nào không nên',
        li: [
          'Khi mô hình kinh doanh không có khả năng lớn gấp nhiều lần. Nhà đầu tư mạo hiểm cần một số ít khoản thắng lớn để bù cho phần lớn khoản thua, nên doanh nghiệp tăng trưởng đều nhưng giới hạn không hợp với họ, dù đó vẫn là một doanh nghiệp tốt.',
          'Khi doanh thu đã đủ nuôi tăng trưởng mong muốn. Gọi vốn tốn nhiều tháng của người sáng lập và lấy đi một phần sở hữu.',
          'Khi chưa sẵn sàng chia sẻ quyền quyết định và báo cáo định kỳ cho cổ đông bên ngoài.',
        ],
      },
      {
        h: 'Các vòng theo thứ tự',
        p: [
          'Startup thường đi qua nhiều vòng, mỗi vòng ứng với một mức bằng chứng: vòng angel (ý tưởng và đội ngũ), [[vong-pre-seed|pre-seed]] (sản phẩm và tín hiệu đầu tiên), [[vong-seed|seed]] (doanh thu đang tăng), rồi [[series-a|Series A]] (mô hình đã kiểm chứng, sẵn sàng mở rộng). Mỗi vòng bán ra thêm một phần công ty, nên tỷ lệ của người sáng lập bị [[pha-loang-co-phan|pha loãng]] dần.',
        ],
      },
      {
        h: 'Một vòng gọi vốn diễn ra thế nào',
        li: [
          'Chuẩn bị: [[pitch-deck-la-gi|pitch deck]], mô hình tài chính, [[bang-co-phan|bảng cổ phần]], hồ sơ pháp lý.',
          'Tìm đúng nhà đầu tư: quỹ và angel có [[khau-vi-dau-tu|khẩu vị]] đúng giai đoạn, đúng ngành.',
          'Tiếp cận, ưu tiên qua [[warm-intro|người giới thiệu]], rồi gặp và trả lời câu hỏi.',
          'Nhận [[term-sheet|term sheet]], đàm phán điều khoản.',
          '[[due-diligence|Thẩm định]], ký hợp đồng, nhận tiền.',
        ],
      },
      {
        h: 'Founder lần đầu thường hiểu sai điều gì',
        li: [
          'Coi gọi vốn là thành công. Gọi được vốn chỉ có nghĩa bạn có thêm thời gian và tiền để chứng minh điều mình nói; áp lực tăng lên, không giảm đi.',
          'Gửi deck cho càng nhiều nhà đầu tư càng tốt. Gửi cho đúng 30 tới 50 người đầu tư đúng giai đoạn, đúng ngành hiệu quả hơn nhiều so với 300 thư gửi hàng loạt.',
          'Nhận tiền của người quen mà không có giấy tờ. Vài trăm triệu "góp vui" không ghi nhận đúng cách sẽ thành rắc rối lớn nhất khi [[due-diligence|thẩm định]] ở vòng sau.',
          'Chỉ nhìn định giá. Điều khoản trong [[term-sheet|term sheet]] (quyền ưu tiên, quyền phủ quyết, ESOP) nhiều khi quan trọng hơn vài tỷ định giá.',
          'Quên chi phí thời gian. Trong 3 tới 6 tháng gọi vốn, người sáng lập dành phần lớn thời gian cho nhà đầu tư, nên doanh nghiệp cần một người khác giữ nhịp vận hành.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Nhà đầu tư bỏ 6 tỷ vào công ty định giá pre-money 24 tỷ. Tiền đó vào đâu, và họ sở hữu bao nhiêu? (Đáp án: vào tài khoản công ty; họ sở hữu 20%.)',
          'Doanh nghiệp của bạn có lãi đều 15% mỗi năm nhưng thị trường khó lớn thêm. Quỹ mạo hiểm có phải nguồn vốn hợp nhất không? (Đáp án: thường là không; vốn vay hoặc tự nuôi bằng doanh thu hợp hơn.)',
          'Bạn còn 4 tháng runway. Có nên bắt đầu gọi vốn bây giờ không? (Đáp án: nên, ngay lập tức, nhưng một vòng thường mất 3 tới 6 tháng nên cần chuẩn bị cả phương án cắt giảm chi phí.)',
        ],
        sau: [
          'Nếu đây là lần đầu bạn gọi vốn, đi theo [[/huong-dan/goi-von-lan-dau|hướng dẫn gọi vốn lần đầu]] hoặc đo mức sẵn sàng bằng [[/cong-cu/diem-san-sang-goi-von|công cụ điểm sẵn sàng gọi vốn]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Gọi vốn là gì?',
        a: 'Là huy động tiền từ bên ngoài cho doanh nghiệp. Với startup, thường là bán một phần cổ phần cho nhà đầu tư để có tiền tăng trưởng nhanh.',
      },
      {
        q: 'Gọi vốn có phải trả lại tiền không?',
        a: 'Nếu bán cổ phần thì không phải trả lại như khoản vay. Nhà đầu tư trở thành cổ đông và chia sẻ rủi ro: công ty thành công thì phần sở hữu của họ tăng giá trị, thất bại thì họ mất tiền.',
      },
      {
        q: 'Startup mới có ý tưởng gọi vốn được không?',
        a: 'Có thể ở vòng angel, chủ yếu từ người quen biết đội ngũ. Nhưng rất khó. Có sản phẩm chạy được và vài khách hàng đầu tiên sẽ thay đổi hoàn toàn cuộc nói chuyện. Xem [[mvp-la-gi|MVP]].',
      },
      {
        q: 'Gọi vốn lần đầu mất bao lâu?',
        a: 'Thường 3 tới 6 tháng từ lúc bắt đầu tiếp cận tới lúc tiền về, chưa kể thời gian chuẩn bị hồ sơ.',
      },
      {
        q: 'Gọi vốn có làm mất quyền kiểm soát công ty không?',
        a: 'Không tự động. Bán 20% cổ phần không có nghĩa mất quyền điều hành, nhưng nhà đầu tư thường có thêm quyền phủ quyết một số việc hoặc ghế hội đồng quản trị theo thỏa thuận cổ đông. Đọc kỹ các điều khoản đó quan trọng hơn con số phần trăm.',
      },
    ],
  },

  'co-phan-chet': {
    capNhat: '2026-10-11',
    lienQuan: ['vesting-la-gi', 'good-leaver-bad-leaver', 'chia-co-phan-dong-sang-lap', 'thoa-thuan-dong-sang-lap', 'bang-co-phan'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Cổ phần chết (dead equity) là phần cổ phần nằm trong tay người không còn đóng góp cho công ty, thường là một đồng sáng lập đã rời đi nhưng vẫn giữ nguyên phần được chia ban đầu.',
          'Phần này vẫn được tính khi chia mọi thứ về sau: lợi nhuận, tiền bán công ty, quyền biểu quyết. Nhưng nó không tạo ra thêm giá trị nào, và người còn ở lại phải làm việc cho cả phần đó.',
        ],
      },
      {
        h: 'Một ví dụ bằng số',
        p: [
          'Ba người sáng lập chia đều mỗi người khoảng 33%. Sau 6 tháng, một người rời đi vì lý do cá nhân, không có [[vesting-la-gi|vesting]], vẫn giữ 33%.',
          'Qua vòng seed bán ra 20%, người này còn khoảng 26,7%. Qua Series A bán thêm 20%, còn khoảng 21,3%. Nghĩa là tới lúc công ty bắt đầu có giá trị lớn, một người chỉ làm 6 tháng đầu vẫn nắm hơn một phần năm công ty, thường còn nhiều hơn cả quỹ ESOP dành cho toàn bộ nhân sự.',
        ],
      },
      {
        h: 'Vì sao nhà đầu tư ngại tới vậy',
        li: [
          'Động lực của đội ngũ. Người ở lại làm việc nhiều năm nhưng sở hữu không hơn người đã đi bao nhiêu, dễ nản và dễ ra đi.',
          'Ít chỗ cho người mới. Muốn tuyển người giỏi bằng cổ phần thì phải lấy từ phần của những người đang làm, vì phần chết không ai đụng được.',
          'Rủi ro biểu quyết. Một cổ đông lớn không còn gắn bó có thể chặn những quyết định cần sự đồng ý của cổ đông, kể cả việc gọi vốn vòng sau.',
          'Tín hiệu về cách quản trị. Có cổ phần chết thường cho thấy đội sáng lập đã không có [[thoa-thuan-dong-sang-lap|thoả thuận]] rõ ràng từ đầu.',
        ],
        sau: [
          'Không có ngưỡng chính thức, nhưng nhiều nhà đầu tư bắt đầu lo khi phần cổ phần chết vượt khoảng 10 tới 15%, và rất khó đầu tư khi nó lên tới 20 tới 30%.',
        ],
      },
      {
        h: 'Cách phòng, và cách chữa',
        p: [
          'Cách phòng duy nhất hiệu quả là ký vesting cho toàn bộ người sáng lập ngay từ đầu, kèm điều khoản xử lý khi có người rời đi ([[good-leaver-bad-leaver|good leaver và bad leaver]]).',
          'Nếu đã xảy ra, có ba hướng xử lý thường gặp, và hướng nào cũng cần thương lượng với người đã rời đi:',
        ],
        li: [
          'Mua lại một phần hoặc toàn bộ, theo giá thoả thuận. Thường phải trả cao hơn mệnh giá nhưng thấp hơn nhiều giá vòng gần nhất.',
          'Ký vesting áp dụng ngược: người đã rời giữ phần tương ứng thời gian đã làm, phần còn lại trả về công ty.',
          'Chuyển một phần sang quỹ [[esop-la-gi|ESOP]] để dùng tuyển người thay thế.',
        ],
        sau: [
          'Nên xử lý trước khi bắt đầu gọi vốn. Làm việc này giữa lúc đang đàm phán với nhà đầu tư thì người rời đi có nhiều lợi thế hơn bạn.',
        ],
      },
      {
        h: 'Dấu hiệu nhận biết sớm',
        li: [
          'Một người sáng lập làm bán thời gian quá lâu, vẫn giữ việc chính ở nơi khác.',
          'Phần chia được chốt khi mới có ý tưởng, chưa ai đánh giá lại khi vai trò thay đổi.',
          'Có người được chia cổ phần đáng kể chỉ vì góp ý tưởng ban đầu hoặc góp một khoản tiền nhỏ.',
        ],
      },
    ],
    faq: [
      {
        q: 'Cổ phần chết là gì?',
        a: 'Là cổ phần do người không còn đóng góp cho công ty nắm giữ, thường là đồng sáng lập đã rời đi mà vẫn giữ nguyên phần được chia.',
      },
      {
        q: 'Bao nhiêu cổ phần chết thì nhà đầu tư bắt đầu lo?',
        a: 'Không có ngưỡng chính thức. Trên khoảng 10 tới 15% thì thường bị hỏi kỹ; từ 20% trở lên thì nhiều quỹ sẽ yêu cầu xử lý trước khi đầu tư.',
      },
      {
        q: 'Người đã rời đi có bắt buộc phải bán lại cổ phần không?',
        a: 'Không, nếu trước đó không có thoả thuận nào. Cổ phần đã cấp thuộc về họ. Vì vậy cách xử lý duy nhất là thương lượng, và vì vậy vesting ký từ đầu quan trọng đến thế.',
      },
    ],
  },

  'pre-money-post-money': {
    capNhat: '2026-10-12',
    lienQuan: ['dinh-gia-startup', 'option-pool-shuffle', 'safe-post-money', 'pha-loang-co-phan', 'bang-co-phan', 'term-sheet'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Pre-money là giá trị công ty ngay trước khi nhận tiền đầu tư. Post-money là pre-money cộng số tiền vừa nhận. Hai con số chênh nhau đúng bằng số tiền gọi được.',
          'Tiếng Việt hay dịch là định giá trước tiền và sau tiền, hoặc trước vốn và sau vốn. fundraising.vn giữ nguyên hai từ gốc, vì đó là chữ ghi trong term sheet, SAFE và hợp đồng đầu tư, và cũng là chữ nhà đầu tư dùng khi trao đổi.',
          'Quy tắc cần nhớ duy nhất: tỷ lệ sở hữu của nhà đầu tư luôn bằng số tiền họ bỏ vào chia cho định giá post-money. Gần như mọi hiểu lầm khi đàm phán đều bắt đầu từ việc quên quy tắc này, hoặc không hỏi rõ con số đang được nói là pre-money hay post-money.',
        ],
      },
      {
        h: 'Công thức và một ví dụ',
        p: [
          'Định giá post-money = định giá pre-money + số tiền đầu tư. Tỷ lệ của nhà đầu tư = số tiền đầu tư chia định giá post-money.',
          'Công ty của bạn được định giá pre-money 24 tỷ, gọi 6 tỷ.',
        ],
        bang: {
          cot: ['Bước', 'Cách tính', 'Kết quả'],
          hang: [
            ['Định giá post-money', '24 tỷ + 6 tỷ', '30 tỷ'],
            ['Tỷ lệ nhà đầu tư', '6 tỷ chia 30 tỷ', '20%'],
            ['Tỷ lệ cổ đông cũ còn lại', '24 tỷ chia 30 tỷ', '80%'],
            ['Giá mỗi cổ phần (công ty đang có 1.000.000 cổ phần)', '24 tỷ chia 1.000.000', '24.000 đồng'],
            ['Số cổ phần mới phát hành', '6 tỷ chia 24.000 đồng', '250.000 cổ phần'],
          ],
        },
        sau: [
          'Lưu ý giá mỗi cổ phần được tính từ định giá pre-money chia cho số cổ phần trước vòng, không phải từ định giá post-money. Đó là lý do người sáng lập không mất cổ phần nào, chỉ có tỷ lệ giảm từ 100% xuống 80%. Bảng đầy đủ trước và sau vòng có ở trang [[bang-co-phan|bảng cổ phần]].',
        ],
      },
      {
        h: '"Định giá 30 tỷ" có thể là hai thương vụ khác nhau',
        p: [
          'Khi nhà đầu tư nói "chúng tôi định giá công ty 30 tỷ và đầu tư 6 tỷ", câu đó chưa đủ để biết bạn bán bao nhiêu.',
        ],
        bang: {
          cot: ['', 'Nếu 30 tỷ là pre-money', 'Nếu 30 tỷ là post-money'],
          hang: [
            ['Định giá pre-money', '30 tỷ', '24 tỷ'],
            ['Định giá post-money', '36 tỷ', '30 tỷ'],
            ['Nhà đầu tư nhận', '6 chia 36, khoảng 16,7%', '6 chia 30, đúng 20%'],
            ['Bạn giữ lại', 'Khoảng 83,3%', '80%'],
          ],
        },
        sau: [
          'Chênh hơn 3 điểm phần trăm chỉ vì một chữ. Với công ty sau này bán được 300 tỷ, 3,3% là khoảng 10 tỷ. Hỏi rõ "pre-money hay post-money" là câu hỏi bình thường, nhà đầu tư nghiêm túc không thấy phiền, và câu trả lời phải được ghi vào [[term-sheet|term sheet]].',
        ],
      },
      {
        h: 'Quỹ ESOP lấy từ phần nào: chỗ định giá bị "co lại"',
        p: [
          'Nhà đầu tư thường yêu cầu công ty dành sẵn một [[esop-la-gi|quỹ cổ phiếu thưởng cho nhân sự (ESOP)]], ví dụ 10% tính trên định giá post-money, và yêu cầu quỹ này được tạo ra trước khi họ vào. Nghĩa là quỹ ESOP nằm trong định giá pre-money, và chỉ cổ đông cũ chịu phần pha loãng đó. Thủ thuật này hay được gọi là [[option-pool-shuffle|option pool shuffle]].',
          'Cùng thương vụ pre-money 24 tỷ, gọi 6 tỷ, thêm điều kiện "quỹ ESOP 10% sau vòng, tính trong định giá pre-money":',
        ],
        bang: {
          cot: ['', 'Không có ESOP', 'ESOP 10% trong định giá pre-money'],
          hang: [
            ['Nhà đầu tư', '20%', '20%'],
            ['Quỹ ESOP', '0%', '10%'],
            ['Người sáng lập', '80%', '70%'],
            ['Giá trị phần người sáng lập trên định giá 30 tỷ', '24 tỷ', '21 tỷ'],
          ],
        },
        sau: [
          'Trên giấy, định giá pre-money vẫn là 24 tỷ. Nhưng phần thực sự thuộc về người sáng lập chỉ được định giá 21 tỷ. Nhiều người gọi đây là định giá pre-money "thực".',
          'Cách xử lý không phải là từ chối ESOP, vì bạn sẽ cần quỹ này để tuyển người giỏi. Hãy đề xuất quy mô quỹ dựa trên kế hoạch tuyển dụng 12 đến 18 tháng tới (ví dụ chỉ cần 6% thì người sáng lập giữ 74%), hoặc đàm phán nâng định giá pre-money để bù lại.',
        ],
      },
      {
        h: 'Khoản SAFE ký từ trước cũng nằm trong định giá pre-money',
        p: [
          'Nếu công ty đã nhận tiền bằng [[safe-la-gi|SAFE]] hoặc [[trai-phieu-chuyen-doi|trái phiếu chuyển đổi]], các khoản này sẽ chuyển thành cổ phần đúng ở vòng gọi vốn có định giá. Theo cách tính thông thường, phần cổ phần chuyển đổi đó cũng nằm trong định giá pre-money.',
          'Ví dụ: năm ngoái bạn nhận 2 tỷ bằng [[safe-post-money|SAFE post-money]] với [[valuation-cap|mức trần định giá]] 20 tỷ, nghĩa là người cầm SAFE sẽ sở hữu 2 chia 20, tức 10% công ty ngay trước vòng mới. Năm nay vòng mới pre-money 24 tỷ, gọi 6 tỷ.',
        ],
        bang: {
          cot: ['', 'Nếu quên khoản SAFE', 'Thực tế'],
          hang: [
            ['Nhà đầu tư mới', '20%', '20%'],
            ['Người cầm SAFE', '0%', '10% nhân 80%, tức 8%'],
            ['Người sáng lập', '80%', '90% nhân 80%, tức 72%'],
          ],
        },
        sau: [
          'Người cầm SAFE vào ở mức 20 tỷ, rẻ hơn nhà đầu tư mới (24 tỷ). Đó là phần thưởng cho việc chịu rủi ro sớm, hoàn toàn hợp lý. Vấn đề chỉ nằm ở chỗ người sáng lập không tính trước. Hãy dựng bảng mô phỏng ngay khi nhận term sheet bằng [[/cong-cu/mo-phong-vong-goi-von|công cụ mô phỏng vòng gọi vốn]].',
        ],
      },
      {
        h: 'Ở Việt Nam, định giá biến thành giấy tờ thế nào',
        p: [
          'Với công ty cổ phần, vòng gọi vốn thường được thực hiện bằng cách phát hành thêm cổ phần cho nhà đầu tư. Cổ phần thường có mệnh giá 10.000 đồng, nhưng nhà đầu tư mua ở giá tính từ định giá pre-money (24.000 đồng trong ví dụ trên). Vốn điều lệ chỉ tăng theo mệnh giá (250.000 cổ phần nhân 10.000 đồng, tức 2,5 tỷ); phần chênh 3,5 tỷ được ghi nhận là thặng dư vốn cổ phần.',
          'Vì vậy đừng ngạc nhiên khi vốn điều lệ sau vòng thấp hơn nhiều so với định giá. [[von-dieu-le|Vốn điều lệ]] là con số pháp lý, định giá là con số thương mại. Nếu công ty còn là công ty TNHH, thường phải chuyển đổi sang công ty cổ phần trước khi nhận vốn theo cách này; xem [[cong-ty-co-phan-goi-von|vì sao startup gọi vốn thường là công ty cổ phần]].',
        ],
      },
      {
        h: 'Năm câu nên hỏi khi nhận một con số định giá',
        li: [
          'Con số này là pre-money hay post-money?',
          'Quỹ ESOP bao nhiêu phần trăm, tính trên pre-money hay post-money, và nằm trong định giá pre-money hay không?',
          'Các khoản SAFE, trái phiếu chuyển đổi đang có được tính vào đâu?',
          'Định giá được tính trên cơ sở [[pha-loang-toan-phan|pha loãng toàn phần]] (gồm cả ESOP và các khoản chuyển đổi) hay chỉ trên cổ phần đã phát hành?',
          'Sau vòng này, bảng cổ phần của chúng tôi trông thế nào? Đề nghị nhà đầu tư gửi kèm bảng tính để hai bên đối chiếu.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Tính tỷ lệ trên định giá pre-money. "Định giá 24 tỷ, bỏ 6 tỷ lấy 25%" là sai: 6 chia 24 không phải cách tính tỷ lệ.',
          'Chỉ đàm phán con số định giá mà bỏ qua ESOP và SAFE. Hai thứ này có thể lấy đi nhiều phần trăm hơn chênh lệch vài tỷ định giá mà bạn cố đòi.',
          'Đánh đồng định giá với giá trị tiền mặt. Định giá 30 tỷ không có nghĩa phần 70% của bạn bán được 21 tỷ hôm nay; cổ phần startup rất khó bán và nhà đầu tư thường có [[liquidation-preference|quyền ưu tiên thanh toán]] đứng trước bạn.',
          'Đòi định giá quá cao ở vòng đầu. Vòng sau phải cao hơn vòng trước, nếu không sẽ thành [[vong-giam-gia|vòng giảm giá]]; xem [[dinh-gia-qua-cao|vì sao định giá quá cao là rủi ro]].',
          'Không ghi rõ trong văn bản. Term sheet và hợp đồng phải ghi cụ thể định giá pre-money, số cổ phần, giá mỗi cổ phần và cơ sở tính.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Nhà đầu tư bỏ 5 tỷ và nhận 20%. Định giá post-money và pre-money là bao nhiêu? (Đáp án: post-money 25 tỷ, pre-money 20 tỷ.)',
          'Pre-money 40 tỷ, gọi 10 tỷ. Nhà đầu tư nhận bao nhiêu phần trăm? (Đáp án: 10 chia 50, tức 20%.)',
          'Bạn đang sở hữu 100%. Term sheet ghi pre-money 24 tỷ, gọi 6 tỷ, quỹ ESOP 10% sau vòng nằm trong định giá pre-money. Bạn còn bao nhiêu? (Đáp án: 70%.)',
        ],
        sau: [
          'Muốn biết công ty mình nên được định giá ở khoảng nào trước khi đàm phán, thử [[/cong-cu/dinh-gia-pre-seed|công cụ định giá pre-seed]]. Muốn xem tỷ lệ của mình thay đổi qua nhiều vòng, dùng [[/cong-cu/bang-co-phan-va-pha-loang|công cụ bảng cổ phần và pha loãng]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Pre-money và post-money là gì?',
        a: 'Pre-money (còn gọi là định giá trước tiền, trước vốn) là giá trị công ty trước khi nhận vốn đầu tư. Post-money (định giá sau tiền, sau vốn) là pre-money cộng số tiền đầu tư. Tỷ lệ của nhà đầu tư bằng số tiền đầu tư chia post-money.',
      },
      {
        q: 'Khi đàm phán, người ta hay nói con số nào?',
        a: 'Ở vòng có định giá, con số hay được nhắc là định giá pre-money. Với SAFE bản chuẩn hiện nay, mức trần định giá là post-money. Vì không có quy ước chung, luôn hỏi rõ và ghi vào văn bản.',
      },
      {
        q: 'Định giá pre-money có bao gồm quỹ ESOP không?',
        a: 'Thường là có, nếu nhà đầu tư yêu cầu quỹ ESOP được tạo trước khi họ vào. Khi đó chỉ cổ đông cũ chịu pha loãng từ ESOP, và định giá thực của phần người sáng lập thấp hơn con số trên giấy.',
      },
      {
        q: 'Làm sao kiểm tra nhanh một con số định giá?',
        a: 'Lấy số tiền đầu tư chia cho tỷ lệ nhà đầu tư nhận. Kết quả phải bằng định giá post-money. Nếu không khớp, có một khoản (ESOP, SAFE, cổ phần chưa tính) chưa được nói ra.',
      },
      {
        q: 'Startup Việt Nam giai đoạn pre-seed thường được định giá bao nhiêu?',
        a: 'Không có con số chung; định giá phụ thuộc vào đội ngũ, thị trường, bằng chứng nhu cầu và số tiền cần gọi. Thay vì tìm "giá thị trường", hãy bắt đầu từ số tiền cần cho 12 đến 18 tháng và tỷ lệ bạn chấp nhận bán (thường 10% đến 25% mỗi vòng), rồi suy ra định giá.',
      },
    ],
  },

  'runway-la-gi': {
    capNhat: '2026-10-12',
    lienQuan: ['burn-rate', 'gross-burn-net-burn', 'so-tien-goi-von', 'cot-moc-goi-von', 'bridge-round', 'diem-hoa-von'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Runway là số tháng công ty còn hoạt động được với tiền mặt đang có, nếu thu chi giữ như hiện tại. Công thức: tiền mặt chia cho số tiền thực sự mất đi mỗi tháng ([[gross-burn-net-burn|net burn]]).',
          'Đây là con số gần như mọi nhà đầu tư hỏi ngay trong buổi gặp đầu, và là con số quyết định bạn đàm phán ở thế mạnh hay thế yếu. Người còn mười hai tháng và người còn ba tháng ngồi vào cùng một bàn với hai vị thế hoàn toàn khác nhau.',
        ],
      },
      {
        h: 'Tính bằng số cụ thể',
        p: [
          'Tài khoản công ty còn 560 triệu. Mỗi tháng chi 168 triệu (lương, văn phòng, phần mềm, quảng cáo), thu về 31 triệu doanh thu.',
        ],
        bang: {
          cot: ['Chỉ số', 'Cách tính', 'Kết quả'],
          hang: [
            ['Gross burn (tổng chi mỗi tháng)', 'Mọi khoản tiền ra', '168 triệu'],
            ['Net burn (mất đi thật sự)', '168 triệu trừ 31 triệu doanh thu', '137 triệu'],
            ['Runway', '560 triệu chia 137 triệu', 'Khoảng 4,1 tháng'],
          ],
        },
        sau: [
          'Điểm dễ nhầm: lấy 560 chia 168 sẽ ra 3,3 tháng, tức bỏ quên doanh thu. Ngược lại, nếu doanh thu đó chưa chắc chắn (một khách lớn có thể ngừng), hãy tính thêm kịch bản thận trọng ở phần dưới. [[burn-rate|Burn rate]] là tốc độ đốt tiền, runway là quãng đường còn lại.',
        ],
      },
      {
        h: 'Một con số là chưa đủ: tính ba kịch bản',
        p: [
          'Runway tính theo tháng vừa rồi chỉ là một điểm. Nhà đầu tư tin bạn hơn khi bạn chủ động đưa ra cả kịch bản xấu. Cùng số liệu trên:',
        ],
        bang: {
          cot: ['Kịch bản', 'Giả định', 'Net burn', 'Runway'],
          hang: [
            ['Hiện tại', 'Thu chi giữ nguyên', '137 triệu', 'Khoảng 4,1 tháng'],
            ['Thận trọng', 'Mất một khách, doanh thu còn 20 triệu; chi tăng 10% do tuyển thêm', 'Khoảng 165 triệu', 'Khoảng 3,4 tháng'],
            ['Thắt chặt', 'Cắt quảng cáo và một vị trí, chi còn 140 triệu', '109 triệu', 'Khoảng 5,1 tháng'],
          ],
        },
        sau: [
          'Kịch bản thắt chặt không phải để làm ngay, mà để biết trước mình có thể kéo thêm bao nhiêu tháng nếu vòng gọi vốn chậm. Bạn có thể nhập số của chính mình vào [[/cong-cu/tinh-runway|công cụ tính runway]] để thấy ba kịch bản cùng lúc.',
        ],
      },
      {
        h: 'Những khoản hay bị quên',
        li: [
          'Lương tháng 13 và thưởng Tết: một tháng chi gần như gấp đôi rơi đúng vào đầu năm.',
          'Thuế phải nộp theo quý, bảo hiểm xã hội nếu đang chậm đóng.',
          'Tiền cọc và gia hạn hợp đồng thuê văn phòng, phí phần mềm trả theo năm.',
          'Công nợ: khách chưa trả tiền thì chưa phải tiền mặt. Doanh thu đã xuất hóa đơn nhưng 60 ngày nữa mới thu được không kéo dài runway tháng này.',
          'Chi phí của chính vòng gọi vốn: luật sư, kiểm toán hoặc soát xét báo cáo tài chính, chuyển đổi loại hình doanh nghiệp nếu cần.',
        ],
      },
      {
        h: 'Còn bao nhiêu tháng thì phải bắt đầu gọi vốn',
        p: [
          'Một vòng angel hoặc pre-seed ở Việt Nam thường mất ba tới sáu tháng tính từ lúc bắt đầu gặp nhà đầu tư tới lúc tiền về tài khoản, có khi lâu hơn. Các bước thường gặp:',
        ],
        bang: {
          cot: ['Giai đoạn', 'Thời gian thường gặp'],
          hang: [
            ['Chuẩn bị deck, mô hình tài chính, danh sách nhà đầu tư', '3 đến 6 tuần'],
            ['Gặp nhà đầu tư cho tới khi có [[term-sheet|term sheet]]', '6 đến 12 tuần'],
            ['[[due-diligence|Thẩm định]], soạn và ký hợp đồng', '3 đến 6 tuần'],
            ['Thủ tục tăng vốn, nhận tiền (lâu hơn nếu nhà đầu tư nước ngoài góp vốn vào công ty Việt Nam)', '2 đến 6 tuần'],
          ],
        },
        sau: [
          'Cộng lại, nên bắt đầu khi còn ít nhất 9 đến 12 tháng runway. Dưới 6 tháng, bạn thường phải nhận điều khoản bên kia đưa ra, vì nhà đầu tư có kinh nghiệm chỉ cần hỏi hai câu (tiền mặt còn bao nhiêu, mỗi tháng mất bao nhiêu) là biết bạn đang vội.',
          'Ngược lại, khi đã gọi được vốn, số tiền nên đủ cho 18 đến 24 tháng runway, đủ để chạm [[cot-moc-goi-von|cột mốc]] của vòng sau và còn dư vài tháng để gọi tiếp. Xem cách tính ở [[so-tien-goi-von|nên gọi bao nhiêu tiền]].',
        ],
      },
      {
        h: 'Câu hỏi quan trọng hơn: công ty có tự sống được không',
        p: [
          'Runway trả lời "còn bao lâu". Câu hỏi tiếp theo là: nếu tăng trưởng giữ như hiện tại, doanh thu có bù được chi phí trước khi hết tiền không? Nếu có, công ty đang ở thế "tự sống được" và gọi vốn là để đi nhanh hơn. Nếu không, gọi vốn là chuyện sống còn và mọi kế hoạch phải xoay quanh việc đó.',
          'Bạn không cần mô hình phức tạp để trả lời. Lấy tốc độ tăng doanh thu ba tháng gần nhất, kéo dài ra trong số tháng runway còn lại, và xem đường doanh thu có cắt đường chi phí không. Điểm cắt đó gọi là [[diem-hoa-von|điểm hòa vốn]].',
        ],
      },
      {
        h: 'Khi runway còn ngắn',
        li: [
          'Nói chuyện với nhà đầu tư hiện hữu trước tiên. Một [[bridge-round|vòng bắc cầu]] nhỏ từ người đã tin bạn thường nhanh hơn nhiều so với tìm người mới.',
          'Thu tiền sớm: ưu đãi cho khách trả trước theo năm, thúc công nợ.',
          'Cắt chi phí theo thứ tự: những gì không ảnh hưởng tới sản phẩm và khách hàng đi trước.',
          'Chủ động nói với đội ngũ. Người giỏi sẽ tự tìm hiểu nếu bạn im lặng, và thường là theo hướng xấu nhất.',
          'Không ký term sheet tệ chỉ vì sợ. Một điều khoản [[liquidation-preference|ưu tiên thanh toán]] nặng hay định giá quá thấp sẽ đi theo công ty qua mọi vòng sau.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Lấy một tháng làm chuẩn. Một khoản chi bất thường làm lệch hẳn con số; trung bình ba tháng ổn định hơn.',
          'Tính runway theo doanh thu kế hoạch. Runway tính theo hiện tại, tăng trưởng để ở kịch bản lạc quan.',
          'Cộng cả tiền "sắp về" từ nhà đầu tư chưa ký. Chưa ký hợp đồng thì chưa phải tiền.',
          'Trộn tiền cá nhân và tiền công ty. Founder tự bù chi phí bằng tiền túi khiến runway trên sổ dài hơn thực tế và làm rối sổ sách khi [[due-diligence|thẩm định]].',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Tiền mặt 900 triệu, chi 200 triệu mỗi tháng, thu 50 triệu. Runway bao nhiêu? (Đáp án: 900 chia 150, tức 6 tháng.)',
          'Vòng gọi vốn dự kiến mất 5 tháng. Với runway 6 tháng, bạn nên làm gì? (Đáp án: bắt đầu ngay, đồng thời chuẩn bị kịch bản thắt chặt và hỏi nhà đầu tư hiện hữu về khả năng bắc cầu.)',
          'Bạn vừa xuất hóa đơn 300 triệu, khách trả sau 60 ngày. Runway tháng này có dài thêm không? (Đáp án: chưa, cho tới khi tiền về tài khoản.)',
        ],
      },
    ],
    faq: [
      {
        q: 'Runway là gì?',
        a: 'Runway là số tháng công ty còn hoạt động được với tiền mặt hiện có, bằng tiền mặt chia cho net burn (tổng chi trừ doanh thu thu về mỗi tháng).',
      },
      {
        q: 'Runway và burn rate khác nhau thế nào?',
        a: 'Burn rate là số tiền mất đi mỗi tháng, runway là số tháng còn lại. Một cái là tốc độ, một cái là quãng đường.',
      },
      {
        q: 'Startup nên có bao nhiêu tháng runway?',
        a: 'Sau khi gọi vốn, thường nhắm 18 đến 24 tháng. Khi còn 9 đến 12 tháng là lúc nên bắt đầu vòng tiếp theo. Dưới 6 tháng là vùng nguy hiểm.',
      },
      {
        q: 'Nên tính runway theo tháng gần nhất hay trung bình?',
        a: 'Trung bình ba tháng cho con số ổn định hơn. Nếu vừa có thay đổi lớn về nhân sự hay chi phí, dùng tháng gần nhất vì nó phản ánh đúng hiện trạng.',
      },
      {
        q: 'Đã có lãi thì runway bằng bao nhiêu?',
        a: 'Khi doanh thu bù được chi phí, runway không còn giới hạn theo mức hiện tại. Vẫn nên chạy kịch bản thận trọng, vì chỉ cần mất một khách lớn hoặc chi phí tăng là quay lại trạng thái đốt tiền.',
      },
    ],
  },

  'pha-loang-co-phan': {
    capNhat: '2026-10-12',
    lienQuan: ['bang-co-phan', 'pre-money-post-money', 'pha-loang-toan-phan', 'option-pool-shuffle', 'chong-pha-loang', 'quyen-mua-them'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Pha loãng cổ phần là việc tỷ lệ sở hữu của bạn giảm xuống khi công ty phát hành thêm cổ phần cho người mới. Số cổ phần bạn cầm không đổi, nhưng nó chiếm phần nhỏ hơn trong một chiếc bánh đã to ra.',
          'Pha loãng không xấu tự thân. Sở hữu 45% của một công ty trị giá 400 tỷ tốt hơn nhiều so với 100% của một công ty trị giá 5 tỷ. Vấn đề chỉ xuất hiện khi bạn bị pha loãng nhiều mà giá trị công ty không tăng tương xứng, hoặc khi bị pha loãng bởi những thứ bạn không tính trước.',
        ],
      },
      {
        h: 'Công thức một dòng',
        p: [
          'Tỷ lệ mới = tỷ lệ cũ nhân (1 trừ tỷ lệ bán ra trong vòng).',
          'Hai người sáng lập mỗi người 50%. Vòng pre-seed bán 20% công ty (gọi 6 tỷ trên [[pre-money-post-money|định giá pre-money]] 24 tỷ). Mỗi người còn 50% nhân 0,8, tức 40%. Nhà đầu tư 20%. Tổng vẫn là 100%.',
          'Mọi cổ đông hiện hữu đều bị pha loãng cùng một tỷ lệ, kể cả nhà đầu tư vòng trước, trừ khi họ dùng [[quyen-mua-them|quyền mua thêm]] để giữ phần của mình.',
        ],
      },
      {
        h: 'Qua ba vòng: phần trăm giảm, giá trị tăng',
        p: [
          'Ví dụ minh họa (số làm tròn, không phải mức chuẩn của thị trường). Nhóm sáng lập bắt đầu với 100%:',
        ],
        bang: {
          chuThich: 'Ví dụ minh họa',
          cot: ['Vòng', 'Bán ra', 'Định giá post-money', 'Nhóm sáng lập còn', 'Giá trị phần sáng lập'],
          hang: [
            ['Pre-seed', '20% cho nhà đầu tư, cộng quỹ ESOP 10% tạo trước vòng', '30 tỷ', '70%', '21 tỷ'],
            ['Seed', '20%', '100 tỷ', '56%', '56 tỷ'],
            ['Series A', '20%', '400 tỷ', 'Khoảng 45%', 'Khoảng 179 tỷ'],
          ],
        },
        sau: [
          'Sau ba vòng, nhóm sáng lập mất hơn một nửa tỷ lệ nhưng giá trị phần của họ tăng gần chín lần. Đó là pha loãng "tốt": mỗi vòng bán ra ở định giá cao hơn vòng trước. Muốn chạy lại bảng này với số của mình, dùng [[/cong-cu/mo-phong-vong-goi-von|công cụ mô phỏng vòng gọi vốn]].',
        ],
      },
      {
        h: 'Bốn nguồn pha loãng, ba nguồn hay bị bỏ sót',
        li: [
          'Cổ phần mới cho nhà đầu tư. Nguồn hiển nhiên, ai cũng tính.',
          'Quỹ [[esop-la-gi|ESOP]]. Nếu nhà đầu tư yêu cầu tạo quỹ trước khi họ vào (nằm trong định giá pre-money), chỉ cổ đông cũ gánh phần pha loãng này. Trong ví dụ trên, ESOP 10% khiến nhóm sáng lập còn 70% thay vì 80%. Xem [[option-pool-shuffle|option pool shuffle]].',
          'Các khoản [[safe-la-gi|SAFE]] và [[trai-phieu-chuyen-doi|trái phiếu chuyển đổi]] cũ. Chúng chuyển thành cổ phần đúng vào vòng có định giá, và phần đó thường do cổ đông cũ gánh. Một SAFE 2 tỷ với mức trần 20 tỷ có thể lấy đi khoảng 8% sau vòng.',
          'Điều khoản [[chong-pha-loang|chống pha loãng]]. Nếu vòng sau định giá thấp hơn vòng trước ([[vong-giam-gia|vòng giảm giá]]), nhà đầu tư cũ có điều khoản này được bù thêm cổ phần, và người sáng lập gánh phần bù đó.',
        ],
      },
      {
        h: 'Tính trên cơ sở nào: đã phát hành hay pha loãng toàn phần',
        p: [
          'Bạn có thể đang nghĩ mình sở hữu 50% vì sổ đăng ký cổ đông ghi vậy. Nhưng nhà đầu tư luôn tính trên cơ sở [[pha-loang-toan-phan|pha loãng toàn phần]]: cộng thêm toàn bộ quỹ ESOP (kể cả phần chưa cấp), các khoản SAFE và trái phiếu sẽ chuyển đổi. Trên cơ sở đó, 50% của bạn có thể chỉ còn 42%.',
          'Hãy luôn giữ [[bang-co-phan|bảng cổ phần]] ở cả hai cách tính, và khi đàm phán thì nói theo cách nhà đầu tư nói.',
        ],
      },
      {
        h: 'Tỷ lệ và quyền kiểm soát ở Việt Nam',
        p: [
          'Nhiều founder cố giữ trên 51% vì nghĩ như vậy là giữ được quyền quyết định. Thực tế phức tạp hơn. Theo Điều 148 Luật Doanh nghiệp 2020, trừ khi điều lệ quy định khác, nghị quyết thông thường của Đại hội đồng cổ đông cần trên 50% số phiếu của cổ đông dự họp, nhưng một nhóm quyết định quan trọng (sửa loại cổ phần, thay đổi ngành nghề, cơ cấu tổ chức, bán tài sản lớn, tổ chức lại, giải thể) cần từ 65% trở lên. Nghĩa là nhóm cổ đông nắm trên 35% phiếu dự họp có thể chặn những quyết định đó.',
          'Ngoài luật, hợp đồng đầu tư thường trao cho nhà đầu tư [[quyen-phu-quyet|quyền phủ quyết]] một số việc và [[ghe-hoi-dong-quan-tri|ghế hội đồng quản trị]], bất kể họ chỉ giữ 10% hay 20%. Quyền kiểm soát thực tế nằm trong [[dieu-le-cong-ty|điều lệ]] và [[thoa-thuan-co-dong|thỏa thuận cổ đông]] nhiều hơn ở con số phần trăm. Đọc kỹ hai văn bản này quan trọng hơn cố giữ thêm vài phần trăm.',
        ],
      },
      {
        h: 'Bao nhiêu là hợp lý',
        li: [
          'Mỗi vòng thường bán ra khoảng 10% đến 25% công ty. Dưới 10% thường là số tiền quá ít để tạo khác biệt; trên 25% bắt đầu khiến nhà đầu tư vòng sau lo lắng.',
          'Mốc tham khảo hay được nhắc: nhóm sáng lập giữ khoảng 70% đến 85% sau vòng đầu và trên 50% sau Series A. Đây là thông lệ, không phải quy định.',
          'Nhà đầu tư vòng sau muốn thấy người sáng lập còn đủ cổ phần để có động lực làm tiếp nhiều năm. Một bảng cổ phần mà người sáng lập chỉ còn dưới 40% sau vài vòng đầu thường khiến việc gọi tiếp khó hơn.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chỉ nhìn một vòng. Mỗi vòng riêng lẻ đều hợp lý, nhưng phải mô phỏng cả chuỗi mới thấy mình còn bao nhiêu khi tới Series A.',
          'Gọi thật ít để giữ tỷ lệ. Thiếu tiền để chạm cột mốc thì vòng sau phải gọi ở định giá không cao hơn, cuối cùng bị pha loãng nhiều hơn.',
          'Chia cổ phần cho cố vấn hoặc người góp sức ngắn hạn mà không có [[vesting-la-gi|vesting]]. Người rời đi vẫn giữ cổ phần, và phần đó pha loãng mọi người còn lại mãi mãi. Xem [[co-phan-chet|cổ phần chết]].',
          'Quên các khoản SAFE đã ký. Ký nhiều SAFE nhỏ, mỗi bản trông vô hại, cộng lại có thể là 20% công ty.',
          'Nghĩ rằng giữ trên 51% là đủ an toàn mà không đọc điều lệ và thỏa thuận cổ đông.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'Bạn sở hữu 60%. Vòng mới bán ra 25%. Bạn còn bao nhiêu? (Đáp án: 60% nhân 0,75, tức 45%.)',
          'Vòng pre-seed bán 20%, kèm quỹ ESOP 10% tạo trước vòng. Nhóm sáng lập từ 100% còn bao nhiêu? (Đáp án: 70%.)',
          'Bạn còn 40% sau vòng seed nhưng công ty được định giá 100 tỷ, so với 100% của công ty 10 tỷ trước đó. Bạn giàu lên hay nghèo đi trên giấy? (Đáp án: từ 10 tỷ lên 40 tỷ.)',
        ],
        sau: [
          'Xem tỷ lệ thực của bạn ngay bây giờ bằng [[/cong-cu/bang-co-phan-va-pha-loang|công cụ bảng cổ phần và pha loãng]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Pha loãng cổ phần là gì?',
        a: 'Là việc tỷ lệ sở hữu của cổ đông hiện hữu giảm xuống khi công ty phát hành thêm cổ phần, ví dụ cho nhà đầu tư mới, cho quỹ ESOP hoặc khi SAFE chuyển đổi. Số cổ phần của họ không đổi, chỉ tỷ lệ giảm.',
      },
      {
        q: 'Tính pha loãng thế nào?',
        a: 'Tỷ lệ mới bằng tỷ lệ cũ nhân với (1 trừ tỷ lệ bán ra trong vòng). Bán 20% thì mọi cổ đông cũ còn 80% tỷ lệ cũ của mình.',
      },
      {
        q: 'Có cách nào tránh pha loãng không?',
        a: 'Chỉ có cách không phát hành thêm cổ phần, tức không gọi vốn cổ phần. Khi đã gọi vốn, mục tiêu không phải tránh pha loãng mà là mỗi lần pha loãng đổi lại giá trị công ty tăng tương xứng.',
      },
      {
        q: 'Nhà đầu tư cũ có bị pha loãng không?',
        a: 'Có, như mọi cổ đông khác, trừ khi họ dùng quyền mua thêm để giữ tỷ lệ, hoặc được bù cổ phần nhờ điều khoản chống pha loãng khi vòng sau định giá thấp hơn.',
      },
      {
        q: 'Sau mỗi vòng founder nên còn bao nhiêu phần trăm?',
        a: 'Thông lệ hay được nhắc là khoảng 70% đến 85% sau vòng đầu và trên 50% sau Series A. Quan trọng hơn con số là người sáng lập còn đủ động lực và quyền điều hành theo điều lệ và thỏa thuận cổ đông.',
      },
    ],
  },

  'safe-la-gi': {
    capNhat: '2026-10-12',
    lienQuan: ['safe-post-money', 'safe-pre-money', 'valuation-cap', 'discount-safe', 'mfn-clause', 'trai-phieu-chuyen-doi'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'SAFE (Simple Agreement for Future Equity, thỏa thuận đơn giản về cổ phần tương lai) là văn bản cho phép nhà đầu tư đưa tiền vào ngay bây giờ và nhận cổ phần ở vòng gọi vốn có định giá sau này. Nó không phải khoản vay: không lãi suất, không ngày đáo hạn, nhà đầu tư không đòi lại tiền theo lịch.',
          'SAFE do Y Combinator, một chương trình tăng tốc ở Mỹ, đưa ra năm 2013, và bản dùng phổ biến hiện nay là bản post-money từ năm 2018. Điểm hấp dẫn: hai bên không phải chốt định giá ngay, thứ rất khó làm khi công ty còn quá sớm, và văn bản ngắn nên ký nhanh, chi phí luật sư thấp.',
        ],
      },
      {
        h: 'Ba điều khoản quyết định mọi thứ',
        bang: {
          cot: ['Điều khoản', 'Nghĩa là gì', 'Ví dụ'],
          hang: [
            ['[[valuation-cap|Mức trần định giá]] (valuation cap)', 'Định giá tối đa dùng để quy đổi SAFE thành cổ phần, dù vòng sau định giá cao bao nhiêu', 'Cap 20 tỷ: nhà đầu tư được tính như thể công ty trị giá 20 tỷ, kể cả khi vòng sau định giá 40 tỷ'],
            ['[[discount-safe|Chiết khấu]] (discount)', 'Nhà đầu tư SAFE mua cổ phần rẻ hơn giá của vòng sau một tỷ lệ, thường 10% đến 25%', 'Vòng sau bán 24.000 đồng một cổ phần, chiết khấu 20% thì người cầm SAFE mua ở 19.200 đồng'],
            ['[[mfn-clause|Điều khoản MFN]]', 'Nếu sau này bạn ký SAFE khác với điều kiện tốt hơn, nhà đầu tư này được hưởng điều kiện đó', 'Thường dùng khi SAFE không có cap'],
          ],
        },
        sau: [
          'Khi SAFE có cả cap lẫn chiết khấu, nhà đầu tư dùng cách nào cho họ nhiều cổ phần hơn, không cộng dồn hai cách.',
        ],
      },
      {
        h: 'Cap hay chiết khấu: cách nào được dùng',
        p: [
          'SAFE 2 tỷ, cap 20 tỷ, chiết khấu 20%. Bảng dưới so sánh mức định giá quy đổi tương ứng (tính gần đúng để thấy nguyên tắc):',
        ],
        bang: {
          chuThich: 'Tính gần đúng, số chính xác phụ thuộc bảng cổ phần cụ thể',
          cot: ['Định giá pre-money vòng sau', 'Theo cap', 'Theo chiết khấu 20%', 'Nhà đầu tư dùng'],
          hang: [
            ['40 tỷ', '20 tỷ', '32 tỷ', 'Cap'],
            ['24 tỷ', '20 tỷ', '19,2 tỷ', 'Chiết khấu (rẻ hơn một chút)'],
            ['15 tỷ', '20 tỷ', '12 tỷ', 'Chiết khấu'],
          ],
        },
        sau: [
          'Nguyên tắc: vòng sau càng cao, cap càng có lợi cho nhà đầu tư. Vòng sau thấp hơn cap thì chiết khấu là thứ bảo vệ họ. Với founder, cap là con số đáng đàm phán nhất.',
        ],
      },
      {
        h: 'Vậy SAFE 2 tỷ cap 20 tỷ là bán bao nhiêu phần trăm',
        p: [
          'Với [[safe-post-money|SAFE post-money]], câu trả lời rõ ràng: 2 chia 20, tức người cầm SAFE sở hữu 10% công ty ngay trước khi tiền vòng mới vào. Vòng mới bán thêm 20% thì phần của họ còn 10% nhân 0,8, tức 8%, và phần người sáng lập từ 90% còn 72%.',
          'Điểm cần hiểu kỹ: với bản post-money, mỗi SAFE giữ cố định tỷ lệ của mình, và toàn bộ pha loãng giữa các SAFE do người sáng lập gánh. Bản [[safe-pre-money|pre-money]] cũ thì các SAFE pha loãng lẫn nhau, khó tính trước hơn.',
        ],
      },
      {
        h: 'Ký nhiều SAFE: cộng lại trước khi ký bản tiếp theo',
        bang: {
          cot: ['Khoản SAFE', 'Số tiền', 'Cap (post-money)', 'Tỷ lệ trước vòng mới'],
          hang: [
            ['Angel đầu tiên', '1 tỷ', '10 tỷ', '10%'],
            ['Angel thứ hai', '2 tỷ', '20 tỷ', '10%'],
            ['Quỹ nhỏ', '1,5 tỷ', '30 tỷ', '5%'],
            ['Cộng', '4,5 tỷ', '', '25%'],
          ],
        },
        sau: [
          'Người sáng lập còn 75% trước khi vòng có định giá diễn ra. Vòng đó bán 20% nữa, người sáng lập còn 60%. Mỗi bản ký riêng lẻ đều nhỏ, nhưng cộng lại là một phần tư công ty. Mô phỏng trước bằng [[/cong-cu/safe-va-trai-phieu-chuyen-doi|công cụ SAFE và trái phiếu chuyển đổi]].',
        ],
      },
      {
        h: 'Điều gì xảy ra nếu không có vòng sau',
        li: [
          'Có vòng gọi vốn có định giá: SAFE chuyển thành cổ phần theo cap hoặc chiết khấu.',
          'Công ty được bán trước khi có vòng mới: theo bản chuẩn, người cầm SAFE được chọn nhận lại số tiền đã đầu tư hoặc nhận phần tương ứng tính theo cap, lấy phần lớn hơn.',
          'Công ty giải thể: người cầm SAFE được hoàn tiền sau các chủ nợ nhưng trước cổ đông phổ thông, trong phạm vi tài sản còn lại.',
          'Công ty sống mãi mà không gọi vốn tiếp: SAFE nằm im. Đây là rủi ro nhà đầu tư chấp nhận khi ký.',
        ],
      },
      {
        h: 'SAFE ở Việt Nam',
        p: [
          'SAFE là mẫu văn bản theo luật Mỹ, pháp luật Việt Nam không có khuôn khổ riêng cho nó. Một khoản tiền vào công ty Việt Nam phải là vốn góp, khoản vay, hoặc một hình thức khác mà luật công nhận, và tiền từ nhà đầu tư nước ngoài còn phải đi qua tài khoản vốn đầu tư theo quy định ngoại hối.',
          'Trên thực tế, các cách hay gặp gồm: ký SAFE với một công ty mẹ ở nước ngoài (xem [[cau-truc-holding-singapore|cấu trúc holding Singapore]]); dùng thỏa thuận đầu tư có điều kiện chuyển đổi được luật sư Việt Nam soạn lại; hoặc dùng khoản vay có quyền chuyển đổi. Mỗi cách có hệ quả thuế, ngoại hối và thủ tục khác nhau. Đây là chỗ bắt buộc phải hỏi luật sư trước khi nhận tiền, không phải sau.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Coi cap là định giá. Cap 20 tỷ không có nghĩa công ty trị giá 20 tỷ; nó chỉ là mức trần để quy đổi sau này.',
          'Không cộng các SAFE lại trước khi ký bản mới.',
          'Nhầm bản pre-money và post-money. Bản post-money có chữ "post-money" ngay trong tiêu đề; không thấy thì khả năng cao bạn đang cầm bản cũ.',
          'Đặt cap quá cao để "giữ giá". Vòng sau định giá không vượt được cap thì nhà đầu tư dùng chiết khấu, và cap cao cũng khiến người đầu tư sớm thấy không đáng chịu rủi ro.',
          'Dùng SAFE không cap, không chiết khấu với người lạ. Nhà đầu tư sớm cần được thưởng cho rủi ro họ chịu; một bản không có cả hai thường là dấu hiệu chưa ai đọc kỹ.',
        ],
      },
      {
        h: 'Tự kiểm tra trong một phút',
        li: [
          'SAFE post-money 1 tỷ, cap 10 tỷ. Người cầm SAFE sở hữu bao nhiêu ngay trước vòng mới? (Đáp án: 10%.)',
          'Vòng mới sau đó bán 25%. Người cầm SAFE còn bao nhiêu? (Đáp án: 10% nhân 0,75, tức 7,5%.)',
          'SAFE có cap 20 tỷ và chiết khấu 20%. Vòng sau định giá pre-money 50 tỷ. Nhà đầu tư dùng cách nào? (Đáp án: cap, vì 20 tỷ thấp hơn 40 tỷ theo chiết khấu.)',
        ],
      },
    ],
    faq: [
      {
        q: 'SAFE là gì?',
        a: 'SAFE là thỏa thuận cho phép nhà đầu tư đưa tiền vào bây giờ và nhận cổ phần ở vòng gọi vốn có định giá sau này, thường theo mức trần định giá (cap) hoặc chiết khấu. SAFE không phải khoản vay.',
      },
      {
        q: 'SAFE khác trái phiếu chuyển đổi ở chỗ nào?',
        a: 'Trái phiếu chuyển đổi là khoản vay, có lãi suất và ngày đáo hạn; tới hạn mà chưa có vòng mới thì về nguyên tắc phải trả tiền. SAFE không có hai thứ đó nên nhẹ hơn cho công ty giai đoạn sớm.',
      },
      {
        q: 'SAFE có hợp pháp ở Việt Nam không?',
        a: 'Pháp luật Việt Nam không có khuôn khổ riêng cho SAFE. Nhiều thương vụ dùng SAFE qua công ty mẹ ở nước ngoài hoặc chuyển thành thỏa thuận đầu tư có điều kiện theo luật Việt Nam. Cần hỏi luật sư trước khi nhận tiền.',
      },
      {
        q: 'Valuation cap thường đặt bao nhiêu?',
        a: 'Không có mức chuẩn. Cap thường gần với định giá bạn kỳ vọng ở vòng có định giá tiếp theo, trừ đi phần thưởng cho rủi ro nhà đầu tư sớm. Tính thử nhiều mức cap để xem mỗi mức lấy đi bao nhiêu phần trăm.',
      },
      {
        q: 'Khi nào SAFE chuyển thành cổ phần?',
        a: 'Ở vòng gọi vốn có định giá tiếp theo. Nếu công ty được bán trước đó, người cầm SAFE nhận lại tiền hoặc phần tương ứng theo cap; nếu giải thể, họ được hoàn tiền trước cổ đông phổ thông trong phạm vi tài sản còn lại.',
      },
    ],
  },

  'due-diligence': {
    capNhat: '2026-10-12',
    lienQuan: ['data-room', 'term-sheet', 'chuyen-giao-so-huu-tri-tue', 'thoa-thuan-co-dong', 'hop-dong-mua-co-phan-spa', 'dong-vong-goi-von'],
    body: [
      {
        h: 'Nói ngắn gọn',
        p: [
          'Thẩm định (due diligence, hay gọi tắt DD) là quá trình nhà đầu tư kiểm tra pháp lý, tài chính, kinh doanh và đội ngũ của công ty để xác nhận những gì bạn đã nói là đúng, trước khi xuống tiền.',
          'Thẩm định thường bắt đầu sau khi hai bên ký [[term-sheet|term sheet]]. Với vòng pre-seed, quá trình này thường mất hai tới bốn tuần nếu hồ sơ gọn, và có thể kéo dài vài tháng nếu phải đi sửa giấy tờ. Phần lớn thương vụ đổ vỡ ở giai đoạn này không phải vì công ty có vấn đề, mà vì vấn đề được phát hiện thay vì được nói ra.',
        ],
      },
      {
        h: 'Thẩm định nằm ở đâu trong một vòng gọi vốn',
        li: [
          'Gặp gỡ và thuyết trình: nhà đầu tư hỏi, bạn trả lời bằng lời và bằng deck.',
          'Term sheet: thống nhất các điều khoản chính. Phần lớn term sheet không ràng buộc về việc đầu tư, nhưng thường có điều khoản độc quyền (bạn không đàm phán với bên khác trong vài tuần) và bảo mật.',
          'Thẩm định: nhà đầu tư kiểm tra giấy tờ, số liệu, gọi điện cho khách hàng.',
          'Hợp đồng: [[hop-dong-mua-co-phan-spa|hợp đồng mua cổ phần]], [[thoa-thuan-co-dong|thỏa thuận cổ đông]], sửa [[dieu-le-cong-ty|điều lệ]]. Những gì thẩm định phát hiện sẽ đi vào phần cam đoan và bảo đảm, hoặc thành điều kiện phải xử lý trước khi giải ngân.',
          'Thủ tục và giải ngân: tăng vốn, cập nhật đăng ký doanh nghiệp, tiền về tài khoản. Xem [[dong-vong-goi-von|đóng vòng gọi vốn]].',
        ],
      },
      {
        h: 'Họ kiểm tra những gì, và bạn cần chuẩn bị gì',
        bang: {
          cot: ['Mảng', 'Câu hỏi nhà đầu tư muốn trả lời', 'Tài liệu thường được yêu cầu'],
          hang: [
            ['Pháp lý doanh nghiệp', 'Công ty có tồn tại hợp lệ, ai sở hữu gì, vốn đã góp đủ chưa', 'Giấy chứng nhận đăng ký doanh nghiệp, điều lệ, sổ đăng ký cổ đông, nghị quyết và biên bản họp, chứng từ góp vốn, [[bang-co-phan|bảng cổ phần]]'],
            ['Sở hữu trí tuệ', 'Sản phẩm, mã nguồn, thương hiệu có thật sự thuộc về công ty không', 'Hợp đồng lao động và hợp đồng với freelancer có điều khoản chuyển giao, giấy tờ [[chuyen-giao-so-huu-tri-tue|chuyển giao sở hữu trí tuệ]] từ founder, đơn [[dang-ky-nhan-hieu|đăng ký nhãn hiệu]]'],
            ['Tài chính và thuế', 'Doanh thu, chi phí có thật và khớp với deck không, có nghĩa vụ thuế nào treo không', 'Báo cáo tài chính, sổ sách kế toán, sao kê ngân hàng, tờ khai và chứng từ nộp thuế, hóa đơn, công nợ'],
            ['Kinh doanh', 'Khách hàng có thật, có dùng và trả tiền không', 'Hợp đồng với khách lớn, số liệu sản phẩm, danh sách khách để nhà đầu tư gọi xác nhận'],
            ['Nhân sự', 'Đội ngũ có cam kết, có rủi ro tranh chấp lao động không', 'Hợp đồng lao động, bảo hiểm xã hội, thỏa thuận [[vesting-la-gi|vesting]] và [[esop-la-gi|ESOP]], [[thoa-thuan-dong-sang-lap|thỏa thuận đồng sáng lập]]'],
            ['Dữ liệu và tuân thủ', 'Công ty có xử lý dữ liệu cá nhân, giấy phép ngành đúng quy định không', 'Chính sách và hồ sơ xử lý dữ liệu cá nhân (xem [[nghi-dinh-13-2023|quy định bảo vệ dữ liệu cá nhân]]), giấy phép con nếu ngành yêu cầu'],
          ],
        },
        sau: [
          'Ở vòng pre-seed, nhà đầu tư thường tập trung nhiều nhất vào hai hàng đầu (ai sở hữu gì, sản phẩm có thuộc về công ty không) và vào con người. Từ vòng seed trở đi, phần tài chính và kinh doanh được soi kỹ hơn nhiều.',
        ],
      },
      {
        h: 'Những điểm hay bị hỏi ở startup Việt Nam',
        li: [
          'Vốn điều lệ đăng ký nhưng chưa góp đủ. Luật Doanh nghiệp 2020 yêu cầu cổ đông thanh toán đủ cổ phần đã đăng ký mua trong 90 ngày kể từ ngày được cấp giấy chứng nhận đăng ký doanh nghiệp. Chưa góp đủ mà không điều chỉnh là điểm phải xử lý trước khi giải ngân.',
          'Mã nguồn do freelancer hoặc công ty ngoài viết mà hợp đồng không có điều khoản chuyển giao quyền sở hữu.',
          'Founder viết sản phẩm trước khi lập công ty và chưa ký giấy chuyển giao cho công ty.',
          'Thu tiền khách qua tài khoản cá nhân, doanh thu chưa xuất hóa đơn đầy đủ, nên số trong deck không khớp số trên sổ sách.',
          'Nhân sự làm việc lâu dài nhưng không có hợp đồng lao động hoặc chưa đóng bảo hiểm xã hội.',
          'Thỏa thuận chia cổ phần giữa các founder chỉ bằng lời, không có vesting; một người đã rời đi nhưng vẫn giữ cổ phần ([[co-phan-chet|cổ phần chết]]).',
          'Thỏa thuận miệng với người góp vốn ban đầu ("anh góp 200 triệu lấy 5%") chưa được ghi nhận trên giấy tờ.',
        ],
      },
      {
        h: 'Điều khiến thẩm định đổ vỡ',
        li: [
          'Phát hiện điều bạn chưa nói. Không phải bản thân vấn đề, mà việc nó bị che giấu khiến nhà đầu tư nghi ngờ mọi thứ khác.',
          'Số liệu không khớp. Doanh thu trong deck khác doanh thu trên sổ, dù chỉ vì cách ghi nhận khác nhau, cũng phải giải thích ngay và rõ.',
          'Quyền sở hữu không rõ: cổ phần chết, sở hữu trí tuệ chưa chuyển giao, tranh chấp với cổ đông cũ. Những thứ này khó sửa nhanh.',
          'Phản hồi chậm. Mỗi yêu cầu tài liệu mất một tuần mới trả lời khiến thương vụ nguội đi, và nhà đầu tư bắt đầu dành thời gian cho công ty khác.',
        ],
      },
      {
        h: 'Cách chuẩn bị: tự thẩm định trước',
        li: [
          'Dựng [[data-room|data room]] trước khi gặp nhà đầu tư: một thư mục chia theo các mảng ở bảng trên, đặt tên tệp rõ ràng, có ngày cập nhật.',
          'Tự rà theo danh sách bằng [[/cong-cu/ho-so-phap-ly-khoi-nghiep|công cụ hồ sơ pháp lý khởi nghiệp]] để biết mình đang thiếu gì.',
          'Đối chiếu ba con số: doanh thu trong deck, trên sổ sách và trên sao kê ngân hàng. Chênh lệch nào cũng phải giải thích được bằng một câu.',
          'Lập danh sách các vấn đề đã biết và cách xử lý. Nói trước với nhà đầu tư, tốt nhất là ngay khi bàn term sheet.',
          'Chuẩn bị sẵn 3 đến 5 khách hàng sẵn lòng nhận cuộc gọi xác nhận.',
          'Hỏi luật sư về những điểm pháp lý lớn (góp vốn, chuyển đổi loại hình, nhà đầu tư nước ngoài) trước khi ký term sheet, không phải sau.',
        ],
      },
      {
        h: 'Sai lầm thường gặp',
        li: [
          'Chờ có term sheet mới bắt đầu gom giấy tờ. Lúc đó mỗi tuần chậm trễ đều làm giảm thế đàm phán.',
          'Gửi toàn bộ tài liệu nhạy cảm trước khi có thỏa thuận bảo mật và trước khi nhà đầu tư thật sự nghiêm túc. Xem [[nda-goi-von|NDA khi gọi vốn]].',
          'Không đàm phán mức trần chi phí. Một số term sheet quy định công ty chịu chi phí luật sư của nhà đầu tư; nên có mức trần cụ thể.',
          'Cố "làm đẹp" số liệu trước thẩm định. Nhà đầu tư sẽ đối chiếu với sao kê ngân hàng và tờ khai thuế.',
        ],
      },
      {
        h: 'Tự kiểm tra: công ty bạn đã sẵn sàng cho thẩm định chưa',
        li: [
          'Bảng cổ phần của bạn có khớp với sổ đăng ký cổ đông và đăng ký doanh nghiệp không?',
          'Mọi cổ đông đã góp đủ vốn đã đăng ký chưa?',
          'Toàn bộ mã nguồn, thiết kế, thương hiệu đã thuộc về công ty bằng văn bản chưa?',
          'Doanh thu trong deck có khớp sổ sách và sao kê ngân hàng không?',
          'Nếu nhà đầu tư gọi cho ba khách hàng ngày mai, bạn có sẵn ba người không?',
        ],
        sau: [
          'Trả lời "chưa" ở câu nào, đó là việc cần làm trước khi gặp nhà đầu tư. Đo mức sẵn sàng tổng thể bằng [[/cong-cu/diem-san-sang-goi-von|công cụ điểm sẵn sàng gọi vốn]].',
        ],
      },
    ],
    faq: [
      {
        q: 'Due diligence là gì?',
        a: 'Due diligence (thẩm định) là quá trình nhà đầu tư kiểm tra pháp lý, tài chính, kinh doanh và đội ngũ của công ty trước khi đầu tư, để xác nhận thông tin công ty đưa ra là đúng.',
      },
      {
        q: 'Thẩm định mất bao lâu?',
        a: 'Hai tới bốn tuần cho vòng pre-seed nếu hồ sơ gọn, lâu hơn nhiều nếu phải sửa giấy tờ. Từ vòng seed trở đi thường kỹ và lâu hơn.',
      },
      {
        q: 'Startup có phải trả chi phí thẩm định không?',
        a: 'Tùy term sheet. Một số thương vụ quy định công ty chịu chi phí luật sư của nhà đầu tư trong một hạn mức. Đây là điều khoản nên đàm phán mức trần.',
      },
      {
        q: 'Phát hiện vấn đề trong thẩm định có mất thương vụ không?',
        a: 'Không nhất thiết. Phần lớn vấn đề sửa được, và nhà đầu tư quen với việc công ty giai đoạn sớm chưa hoàn hảo. Thứ khó cứu nhất là việc thiếu trung thực.',
      },
      {
        q: 'Data room cần những gì?',
        a: 'Tối thiểu gồm hồ sơ pháp lý doanh nghiệp, bảng cổ phần, giấy tờ sở hữu trí tuệ, báo cáo tài chính và thuế, hợp đồng khách hàng lớn, hợp đồng lao động và các thỏa thuận cổ đông, sắp xếp theo từng mảng.',
      },
    ],
  },
};
